import React, { useEffect, useMemo, useRef, useState, useCallback, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB, pl as plPL } from 'date-fns/locale';
import { utcToZonedTime } from 'date-fns-tz';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import allLocales from '@fullcalendar/core/locales-all';
import type { CalendarApi, DateSelectArg, DatesSetArg } from '@fullcalendar/core';
import { BookingSlotsConfig, RoomSummary } from '../../models/Room';
import { CalendarEvent } from '../../models/CalendarEvent';
import { apiClient } from '../../publicApiClient';
import { useTranslation } from 'react-i18next';
import { parseBookingCalendarInstant } from '../../utils/bookingInstant';
import {
  buildDurationOptionsMinutes,
  ceilToStartTimeCardinality,
  formatFullCalendarSlotDuration,
  getEffectiveBookingSlotsConfig,
  snapDurationToOptions,
} from '../../utils/bookingSlotsConfig';
import {
  appendBookingSession,
  BOOKING_REPEAT_MAX_SESSIONS,
  bookingSessionsOverlap,
  resizeWeeklySessions,
} from '../../utils/bookingRepeatSlots';
import BookingOccurrenceEditor from '../booking/BookingOccurrenceEditor';
import {
  firstOpenDateOnOrAfter,
  formatDayMonthYear,
  scheduleForCalendarDate,
} from '../../utils/bookingOpenDay';

/** Weekly recurring occurrences. undefined/null = single booking. */
export type RecurringWeeks = number | undefined;

interface TimeAndDateStepProps {
  room: RoomSummary;
  /**
   * Organization (or mirrored) booking slot constraints.
   * Do not read this from room fields — room writes no longer carry slot config.
   */
  bookingSlotsConfig?: BookingSlotsConfig | null;
  initialDateTime?: Date;
  initialDuration?: number;
  showRecurringControls?: boolean;
  onValidityChange: (isValid: boolean) => void;
  onBookingConfirmed?: (booking: {
    date: Date;
    time: Date;
    duration: number;
    recurringWeeks?: RecurringWeeks;
    /** Every session, including the first. Length 1 means a single booking. */
    sessionStarts?: Date[];
  }) => void;
  /** When set, busy slots are loaded here instead of the room calendar API (e.g. tutor availability). */
  loadCalendarEventsOverride?: (rangeStart: Date, rangeEnd: Date) => Promise<CalendarEvent[]>;
  /** When set, duration is fixed (e.g. lesson length) and the duration slider is hidden unless replaced. */
  fixedDurationMinutes?: number;
  /** Replaces the duration slider block (e.g. lesson option picker). */
  durationSliderReplacement?: ReactNode;
  /**
   * When provided, "Add session" is hidden in the step body and the parent
   * can render it (e.g. dialog footer). Cleared when unavailable.
   */
  onAddSessionControlsChange?: (
    controls: { canAdd: boolean; addSession: () => void } | null,
  ) => void;
}

export default function TimeAndDateStep({
  room,
  bookingSlotsConfig = null,
  initialDateTime,
  initialDuration,
  showRecurringControls = true,
  onValidityChange,
  onBookingConfirmed,
  loadCalendarEventsOverride,
  fixedDurationMinutes,
  durationSliderReplacement,
  onAddSessionControlsChange,
}: TimeAndDateStepProps) {
  const { t } = useTranslation();
  const calendarRef = useRef<FullCalendar>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // --- add a ref for the controls/header block ---
  const controlsRef = useRef<HTMLDivElement>(null);
  const [calculatedHeight, setCalculatedHeight] = useState(400);

  // Calculate available height for calendar - measured approach
  useEffect(() => {
    const calc = () => {
      // Prefer the actual container height when available
      const containerH = containerRef.current?.getBoundingClientRect().height;
      const screenH = window.innerHeight;
      const dialogH = screenH * (screenH <= 800 ? 0.98 : 0.6);
      const baseH = typeof containerH === 'number' && containerH > 0 ? containerH : dialogH;

      // measure the actual height above the calendar
      const headerH = controlsRef.current?.getBoundingClientRect().height ?? 0;

      // also account for paddings around the calendar
      const paddings = 24; // tweak if needed

      const available = Math.max(200, baseH - headerH - paddings);
      setCalculatedHeight(Math.floor(available));
    };

    // ResizeObserver so it adapts when the controls wrap at <600px
    const ro = new ResizeObserver(calc);
    if (controlsRef.current) ro.observe(controlsRef.current);
    if (containerRef.current) ro.observe(containerRef.current);

    // run on next frame to ensure layout settled
    requestAnimationFrame(calc);
    window.addEventListener('resize', calc);
    return () => {
      window.removeEventListener('resize', calc);
      ro.disconnect();
    };
  }, []);
  
  // Helper function to map locale codes to FullCalendar format
  const getFullCalendarLocale = (locale: string) => {
    const localeMap: Record<string, string> = {
      'en': 'en',
      'pl': 'pl',
      'ru': 'ru'
    };
    return localeMap[locale] || 'en';
  };
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [view, setView] = useState<'timeGridDay' | 'timeGridWeek'>(() => {
    // Default to 'timeGridDay' on mobile, 'timeGridWeek' on desktop
    return window.innerWidth <= 768 ? 'timeGridDay' : 'timeGridWeek';
  });
  
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [visibleRangeVersion, setVisibleRangeVersion] = useState(0);
  
  // Track the currently visible date range on the calendar
  const visibleRangeRef = useRef<{ start: number; end: number } | null>(null);

  // Initialize with current time if no initialDateTime is provided
  const defaultDateTime = useMemo(() => {
    const now = new Date();
    // Round to the nearest 30 minutes
    const minutes = Math.ceil(now.getMinutes() / 30) * 30;
    now.setMinutes(minutes);
    now.setSeconds(0);
    now.setMilliseconds(0);
    
    // If we have room schedule, find first available slot for today
    if (room?.spaceWeeklySchedule) {
      const dayOfWeek = now.getDay();
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
      const scheduleKey = dayNames[dayOfWeek];
      const schedule = room.spaceWeeklySchedule[scheduleKey];
      
      if (schedule && !schedule.dayOff) {
        // Define working window
        let windowStart = new Date(now);
        let windowEnd = new Date(now);
        
        if (schedule.openAllDay) {
          windowStart.setHours(0, 0, 0, 0);
          windowEnd.setHours(23, 59, 0, 0);
        } else {
          const [h0, m0] = schedule.periodStart.split(':').map(Number);
          const [h1, m1] = schedule.periodEnd.split(':').map(Number);
          
          windowStart.setHours(h0, m0, 0, 0);
          windowEnd.setHours(h1, m1, 0, 0);
          
          // Handle overnight schedules
          if (h1 < h0 || (h1 === h0 && m1 < m0)) {
            windowEnd.setDate(windowEnd.getDate() + 1);
          }
        }
        
        // Start from the rounded time or working window start, whichever is later
        const searchStart = new Date(Math.max(now.getTime(), windowStart.getTime()));
        
        // Round search start to next 30-minute boundary
        const searchMinutes = Math.ceil(searchStart.getMinutes() / 30) * 30;
        searchStart.setMinutes(searchMinutes);
        searchStart.setSeconds(0);
        searchStart.setMilliseconds(0);
        
        // If rounded time is before search start, move to next 30-minute slot
        if (now.getTime() < searchStart.getTime()) {
          return searchStart;
        }
      }
    }
    
    return now;
  }, [room?.spaceWeeklySchedule, visibleRangeVersion]);

  // For date, we only want the date part
  const [newDate, setNewDate] = useState<Date | null>(() => {
    if (initialDateTime) {
      const date = new Date(initialDateTime);
      if (!isNaN(date.getTime())) {
        const schedule = scheduleForCalendarDate(room?.spaceWeeklySchedule, date);
        if (schedule?.dayOff) {
          return firstOpenDateOnOrAfter(room?.spaceWeeklySchedule, date) ?? defaultDateTime;
        }
        return date;
      }
    }
    return defaultDateTime;
  });

  const [datePickerValue, setDatePickerValue] = useState<Date | null>(() => {
    if (initialDateTime) {
      const date = new Date(initialDateTime);
      if (!isNaN(date.getTime())) {
        const schedule = scheduleForCalendarDate(room?.spaceWeeklySchedule, date);
        if (schedule?.dayOff) {
          return firstOpenDateOnOrAfter(room?.spaceWeeklySchedule, date) ?? defaultDateTime;
        }
        return date;
      }
    }
    return defaultDateTime;
  });

  const minSelectableDate = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const datePickerDayValue = useMemo(() => {
    if (!datePickerValue || Number.isNaN(datePickerValue.getTime())) return null;
    return new Date(
      datePickerValue.getFullYear(),
      datePickerValue.getMonth(),
      datePickerValue.getDate(),
    );
  }, [
    datePickerValue ? datePickerValue.getFullYear() : null,
    datePickerValue ? datePickerValue.getMonth() : null,
    datePickerValue ? datePickerValue.getDate() : null,
  ]);

  // For time, we want to preserve hours and minutes
  const [newTime, setNewTime] = useState<Date | null>(() => {
    if (initialDateTime) {
      const time = new Date(initialDateTime);
      if (!isNaN(time.getTime())) {
        return new Date(
          time.getFullYear(),
          time.getMonth(),
          time.getDate(),
          time.getHours(),
          time.getMinutes(),
          0,
          0,
        );
      }
    }
    return defaultDateTime;
  });

  const newDateRef = useRef(newDate);
  const newTimeRef = useRef(newTime);
  newDateRef.current = newDate;
  newTimeRef.current = newTime;

  const [newDuration, setNewDuration] = useState<number>(
    (fixedDurationMinutes ?? initialDuration) || 60
  );
  /** Starts after the first session. Empty means a single booking. */
  const [extraStarts, setExtraStarts] = useState<Date[]>([]);
  const recurringWeeks: RecurringWeeks = extraStarts.length > 0 ? extraStarts.length + 1 : undefined;

  useEffect(() => {
    if (fixedDurationMinutes != null && fixedDurationMinutes > 0) {
      setNewDuration(fixedDurationMinutes);
    }
  }, [fixedDurationMinutes]);
  const [, setShowDayOffWarning] = useState<boolean>(false);
  const [, setShowWorkingHoursWarning] = useState<boolean>(false);
  const [, setShowBeforeOpeningWarning] = useState<boolean>(false);

  // When the requested slot falls on a closed weekday, move to the next open day
  // and keep the calendar visible. Hiding it left a blank panel that pointed at a form
  // which is no longer on the right.
  useEffect(() => {
    if (initialDateTime && room) {
      const date = new Date(initialDateTime);
      if (!isNaN(date.getTime())) {
        const schedule = scheduleForCalendarDate(room.spaceWeeklySchedule, date);

        const alreadyOnSlot =
          newDateRef.current != null &&
          newTimeRef.current != null &&
          newDateRef.current.getFullYear() === date.getFullYear() &&
          newDateRef.current.getMonth() === date.getMonth() &&
          newDateRef.current.getDate() === date.getDate() &&
          newTimeRef.current.getHours() === date.getHours() &&
          newTimeRef.current.getMinutes() === date.getMinutes();
        if (alreadyOnSlot && !schedule?.dayOff) {
          return;
        }

        if (schedule?.dayOff) {
          const current = newDateRef.current;
          if (current && !scheduleForCalendarDate(room.spaceWeeklySchedule, current)?.dayOff) {
            return;
          }
          const next = firstOpenDateOnOrAfter(room.spaceWeeklySchedule, date);
          if (!next) return;
          const time = new Date(initialDateTime);
          setNewDate(next);
          setDatePickerValue(next);
          setNewTime(
            new Date(
              next.getFullYear(),
              next.getMonth(),
              next.getDate(),
              time.getHours(),
              time.getMinutes(),
              0,
              0,
            ),
          );
          setShowDayOffWarning(true);
          return;
        }

        setNewDate(date);
        setDatePickerValue(date);
        setShowDayOffWarning(false);

        const time = new Date(initialDateTime);
        setNewTime(
          new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            time.getHours(),
            time.getMinutes(),
            0,
            0,
          ),
        );
      }
    }
  }, [initialDateTime, room]);

  const closedRequestedDateLabel = useMemo(() => {
    if (!initialDateTime || !room?.spaceWeeklySchedule) return null;
    const date = new Date(initialDateTime);
    if (Number.isNaN(date.getTime())) return null;
    if (!scheduleForCalendarDate(room.spaceWeeklySchedule, date)?.dayOff) return null;
    const selected = datePickerValue ?? newDate;
    if (
      selected &&
      selected.getFullYear() === date.getFullYear() &&
      selected.getMonth() === date.getMonth() &&
      selected.getDate() === date.getDate()
    ) {
      return null;
    }
    return formatDayMonthYear(date);
  }, [initialDateTime, room?.spaceWeeklySchedule, datePickerValue, newDate]);

  // Calculate preview start time
  const previewStart = useMemo<Date | null>(() => {
    if (!newDate || !newTime) return null;
    return new Date(
      newDate.getFullYear(),
      newDate.getMonth(),
      newDate.getDate(),
      newTime.getHours(),
      newTime.getMinutes()
    );
  }, [newDate, newTime]);

  const onBookingConfirmedRef = useRef(onBookingConfirmed);
  onBookingConfirmedRef.current = onBookingConfirmed;
  const lastNotifiedSelectionKeyRef = useRef<string>('');

  useEffect(() => {
    if (!showRecurringControls && extraStarts.length > 0) {
      setExtraStarts([]);
    }
  }, [showRecurringControls, extraStarts.length]);

  useEffect(() => {
    if (!previewStart || !newDate) return;
    const sessionStarts = showRecurringControls ? [previewStart, ...extraStarts] : [previewStart];
    const key = [
      previewStart.getFullYear(),
      previewStart.getMonth() + 1,
      previewStart.getDate(),
      previewStart.getHours(),
      previewStart.getMinutes(),
      newDuration,
      recurringWeeks ?? '',
      sessionStarts.map((start) => start.getTime()).join(','),
    ].join('-');
    if (lastNotifiedSelectionKeyRef.current === key) return;
    lastNotifiedSelectionKeyRef.current = key;
    onBookingConfirmedRef.current?.({
      date: newDate,
      time: previewStart,
      duration: newDuration,
      recurringWeeks: showRecurringControls ? recurringWeeks : undefined,
      sessionStarts,
    });
  }, [previewStart, newDate, newDuration, recurringWeeks, extraStarts, showRecurringControls]);


  // Fetch calendar events for a given date range
  const fetchEventsForRange = useCallback(
    (startDate: Date, endDate: Date) => {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);

      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      if (loadCalendarEventsOverride) {
        loadCalendarEventsOverride(start, end).then(setCalendarEvents).catch(console.error);
        return;
      }

      if (!room) return;

      apiClient
        .getRoomCalendarEvents(
          room.id,
          start.toISOString().replace('Z', ''),
          end.toISOString().replace('Z', '')
        )
        .then(setCalendarEvents)
        .catch(console.error);
    },
    [room, loadCalendarEventsOverride]
  );

  // ─── Hooks (all at top) ────────────────────────────────────────────────────
  const effectiveBookingSlotsConfig = useMemo(
    () => getEffectiveBookingSlotsConfig(bookingSlotsConfig),
    [bookingSlotsConfig],
  );
  const startTimeStepMinutes = effectiveBookingSlotsConfig.startTimeCardinalityInMinutes;
  const calendarSlotDuration = useMemo(
    () => formatFullCalendarSlotDuration(startTimeStepMinutes),
    [startTimeStepMinutes],
  );
  // Duration options from organization bookingSlotsConfig (null → unrestricted 30-min fallback)
  const durationOptions = useMemo(
    () => buildDurationOptionsMinutes(bookingSlotsConfig, 360),
    [bookingSlotsConfig],
  );
  const durationLabels = useMemo(() => {
    const labels: Record<number, string> = {};
    durationOptions.forEach(mins => {
      // Only show labels on full hours
      if (mins % 60 === 0) {
        const hrs = mins / 60;
        labels[mins] = `${hrs}h`;
      } else {
        labels[mins] = '';
      }
    });
    return labels;
  }, [durationOptions]);

  useEffect(() => {
    if (fixedDurationMinutes != null) return;
    setNewDuration((prev) => snapDurationToOptions(prev, durationOptions));
  }, [durationOptions, fixedDurationMinutes]);

  // Format duration for display (shows all values including half-hours)
  const formatDuration = useMemo(() => {
    const hrs = Math.floor(newDuration / 60);
    const mins = newDuration % 60;
    const parts = [
      hrs > 0 ? `${hrs}h` : null,
      mins > 0 ? `${mins}m` : null,
    ].filter(Boolean);
    return parts.join(' ') || '30m';
  }, [newDuration]);

  const scrollTime = useMemo(() => {
    if (!newTime) return '06:00:00';
    try {
    const hh = String(newTime.getHours()).padStart(2, '0');
    const mm = String(newTime.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}:00`;
    } catch (error) {
      return '06:00:00';
    }
  }, [newTime]);

  // Determine pickable window from spaceWeeklySchedule
  const dayNames = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ] as const;
  const scheduleKey = dayNames[(newDate ?? new Date()).getDay()];
  const sched = useMemo(
    () =>
      room?.spaceWeeklySchedule?.[scheduleKey] ?? {
        periodStart: '06:00',
        periodEnd: '22:00',
        openAllDay: false,
        dayOff: false,
      },
    [room, scheduleKey]
  );

  const scheduleEnd = useMemo<Date | null>(() => {
    if (!newDate) return null;
    const d = new Date(newDate);
    if (sched.openAllDay || sched.dayOff) {
      d.setHours(23, 59, 0, 0);
    } else {
      const [hEnd, mEnd] = sched.periodEnd.split(':').map(Number);
      // Handle overnight schedules (e.g., 10:00 to 03:00)
      if (hEnd < 12) {
        // If end time is early morning (before noon), it's overnight
        d.setDate(d.getDate() + 1); // Move to next day
      }
      d.setHours(hEnd, mEnd, 0, 0);
    }
    return d;
  }, [newDate, sched]);

  // Only navigate to new date when the date actually changes (not just time)
  const [lastNavigatedDate, setLastNavigatedDate] = useState<string | null>(null);

  useEffect(() => {
    const api: CalendarApi | null = calendarRef.current?.getApi?.() ?? null;
    const dt = newDate;
    if (!api || !dt || !scrollTime) return;
    
    // Only navigate if the date (not time) has changed
    const currentDateString = dt.toDateString();
    if (lastNavigatedDate !== currentDateString) {
      try {
    api.gotoDate(dt);
        setLastNavigatedDate(currentDateString);
      } catch (error) {
        console.warn('FullCalendar API error:', error);
      }
    }
    
    // Always scroll to time when it changes
    try {
    api.scrollToTime(scrollTime);
    } catch (error) {
      console.warn('FullCalendar scroll error:', error);
    }
  }, [newDate, scrollTime, lastNavigatedDate]);

  // Handle view changes
  useEffect(() => {
    const api: CalendarApi | null = calendarRef.current?.getApi?.() ?? null;
    if (!api) return;
    api.changeView(view);
  }, [view]);

  // Handle window resize to change default view
  useEffect(() => {
    const handleResize = () => {
      const isMobileView = window.innerWidth <= 768;
      setIsMobile(isMobileView);
      const newView = isMobileView ? 'timeGridDay' : 'timeGridWeek';
      if (newView !== view) {
        setView(newView);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [view]);

  // ─── Compute slotMin & slotMax for FullCalendar ─────────────────────────
  // Calculate the full range across all days of the week
  const getFullTimeRange = () => {
    if (!room?.spaceWeeklySchedule) return { slotMin: '00:00:00', slotMax: '24:00:00' };
    
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    let earliestStart = '24:00:00';
    let latestEnd = '00:00:00';
    let hasOpenAllDay = false;
    
    for (const dayName of dayNames) {
      const schedule = room.spaceWeeklySchedule[dayName];
      if (schedule && !schedule.dayOff) {
        if (schedule.openAllDay) {
          hasOpenAllDay = true;
          // For 24-hour days, consider the full day range
          if ('00:00' < earliestStart) {
            earliestStart = '00:00';
          }
          if ('24:00' > latestEnd) {
            latestEnd = '24:00';
          }
        } else {
          if (schedule.periodStart < earliestStart) {
            earliestStart = schedule.periodStart;
          }
          if (schedule.periodEnd > latestEnd) {
            latestEnd = schedule.periodEnd;
          }
        }
      }
    }
    
    // If we have any 24-hour days, ensure we cover the full range
    if (hasOpenAllDay) {
      if (earliestStart !== '00:00') {
        earliestStart = '00:00';
      }
      if (latestEnd !== '24:00') {
        latestEnd = '24:00';
      }
    }
    
    // If no valid schedules found, use default range
    if (earliestStart === '24:00:00' || latestEnd === '00:00:00') {
      return { slotMin: '00:00:00', slotMax: '24:00:00' };
    }
    
    return { slotMin: `${earliestStart}:00`, slotMax: `${latestEnd}:00` };
  };

  const { slotMin, slotMax } = getFullTimeRange();

  // Function to check if a time slot is available for a specific day
  const isSlotAvailable = (date: Date, time: string): boolean => {
    if (!room?.spaceWeeklySchedule) return true;
    
    const dayOfWeek = date.getDay();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const scheduleKey = dayNames[dayOfWeek];
    const schedule = room.spaceWeeklySchedule[scheduleKey];
    
    if (!schedule || schedule.dayOff) return false;
    if (schedule.openAllDay) return true;
    
    const [timeHour, timeMinute] = time.split(':').map(Number);
    const timeInMinutes = timeHour * 60 + timeMinute;
    
    const [startHour, startMinute] = schedule.periodStart.split(':').map(Number);
    const [endHour, endMinute] = schedule.periodEnd.split(':').map(Number);
    const startTimeInMinutes = startHour * 60 + startMinute;
    const endTimeInMinutes = endHour * 60 + endMinute;
    
    // Handle overnight schedules
    if (endTimeInMinutes < startTimeInMinutes) {
      return timeInMinutes >= startTimeInMinutes || timeInMinutes <= endTimeInMinutes;
  } else {
      return timeInMinutes >= startTimeInMinutes && timeInMinutes <= endTimeInMinutes;
  }
  };

  // ─── Post-load Calculations ─────────────────────────────────────────────
  const existingEvents = (calendarEvents || []).map((ev) => {
    const utcStart = parseBookingCalendarInstant(ev.startTime);
    const utcEnd = parseBookingCalendarInstant(ev.endTime);
    const calendarTimeZone = room?.spaceTimeZone?.trim() || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const localStart = utcToZonedTime(utcStart, calendarTimeZone);
    const localEnd = utcToZonedTime(utcEnd, calendarTimeZone);
    const isPending = loadCalendarEventsOverride && ev.status === 'AWAITING_CONFIRMATION';
    return {
      // Public booking calendar must not expose other guests' event titles / contact details.
      title: t(
        isPending ? 'spaceCalendar.pending' : 'spaceCalendar.booked',
        isPending ? 'Pending' : 'Booked',
      ),
      start: localStart,
      end: localEnd,
      startDate: utcStart,
      endDate: utcEnd,
      backgroundColor: isPending ? '#ff9800' : '#f44336',
      borderColor: isPending ? '#f57c00' : '#d32f2f',
      textColor: '#ffffff',
      classNames: ['existing-booking'],
    };
  });

  // Build available start times dropdown respecting schedule, duration, existing events and past
  const availableStartTimes = useMemo(() => {
    if (!newDate) return [] as Date[];

    const options: Date[] = [];
    const dayOfWeek = newDate.getDay();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const scheduleKey = dayNames[dayOfWeek];
    const schedule = room?.spaceWeeklySchedule?.[scheduleKey];
    if (!schedule || schedule.dayOff) return [];

    // Working window
    const windowStart = new Date(newDate);
    const windowEnd = new Date(newDate);

    if (schedule.openAllDay) {
      windowStart.setHours(0, 0, 0, 0);
      windowEnd.setHours(24, 0, 0, 0);
    } else {
      const [h0, m0] = schedule.periodStart.split(':').map(Number);
      const [h1, m1] = schedule.periodEnd.split(':').map(Number);

      windowStart.setHours(h0, m0, 0, 0);

      // handle overnight (end after midnight)
      windowEnd.setHours(h1, m1, 0, 0);
      if (h1 < h0 || (h1 === h0 && m1 < m0)) {
        // next day
        windowEnd.setDate(windowEnd.getDate() + 1);
      }
    }

    const stepMinutes = getEffectiveBookingSlotsConfig(bookingSlotsConfig)
      .startTimeCardinalityInMinutes;
    const stepMs = stepMinutes * 60 * 1000;

    // If same day, do not allow times in the past. Align to start-of-day cardinality.
    const now = new Date();
    let minStart = ceilToStartTimeCardinality(windowStart, stepMinutes);
    if (minStart < windowStart) {
      minStart = new Date(minStart.getTime() + stepMs);
    }
    if (newDate.toDateString() === now.toDateString() && now > minStart) {
      minStart = ceilToStartTimeCardinality(now, stepMinutes);
      if (minStart < now) {
        minStart = new Date(minStart.getTime() + stepMs);
      }
    }

    for (let t = minStart.getTime(); t <= windowEnd.getTime(); t += stepMs) {
      const start = new Date(t);
      const end = new Date(t + newDuration * 60 * 1000);

      // End must be within working window
      if (end.getTime() > windowEnd.getTime()) break;

      // Check conflicts with existing bookings (compare local start/end)
      const conflicts = existingEvents.some(ev => ev.start < end && ev.end > start);
      if (conflicts) continue;

      options.push(start);
    }

    return options;
  }, [newDate, newDuration, existingEvents, room?.spaceWeeklySchedule, bookingSlotsConfig]);

  // Auto-pick the first available start time if current time is not available
  // If no times available for current date, find next available date
  useEffect(() => {
    const isSameHM = (a: Date, b: Date) => a.getHours() === b.getHours() && a.getMinutes() === b.getMinutes();
    const isSameDate = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    
    if (availableStartTimes.length === 0 && newDate) {
      // No available times for current date, find next available date
      const findNextAvailableDate = () => {
        if (!room?.spaceWeeklySchedule) return null;
        
        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
        
        // Check up to 14 days ahead to be safe
        for (let i = 1; i <= 14; i++) {
          const candidate = new Date(newDate);
          candidate.setDate(newDate.getDate() + i);
          const dayOfWeek = candidate.getDay();
          const scheduleKey = dayNames[dayOfWeek];
          const schedule = room.spaceWeeklySchedule[scheduleKey];
          if (schedule && !schedule.dayOff) {
            return candidate;
          }
        }
        return null;
      };
      
      const nextDate = findNextAvailableDate();
      if (nextDate && !isSameDate(nextDate, newDate)) {
        setNewDate(nextDate);
        setDatePickerValue(nextDate);
      }
      return;
    }
    
    if (availableStartTimes.length > 0) {
      if (!newTime || !availableStartTimes.some(t => isSameHM(t, newTime))) {
        const first = availableStartTimes[0];
        if (!newTime || !isSameHM(first, newTime)) {
          setNewTime(first);
        }
      }
    }
  }, [availableStartTimes, newDate, newTime, room?.spaceWeeklySchedule]);

  const previewEvt =
    previewStart && newDuration > 0
      ? {
          title: t('spaceCalendar.yourBooking'),
          // Use local time dates for FullCalendar display
          start: previewStart,
          end: new Date(previewStart.getTime() + newDuration * 60000),
          backgroundColor: '#4E67EB',
          borderColor: '#4E67EB',
          color: '#ffffff',
          editable: true,
          durationEditable: !(
            typeof fixedDurationMinutes === 'number' && fixedDurationMinutes > 0
          ),
          id: 'preview-booking',
          classNames: ['preview-booking']
        }
      : null;

  const extraPreviewEvents = (showRecurringControls ? extraStarts : []).map((start, index) => ({
    title: t('booking.sessionNumber', 'Session {{number}}', { number: index + 2 }),
    start,
    end: new Date(start.getTime() + newDuration * 60000),
    backgroundColor: '#4E67EB',
    borderColor: '#4E67EB',
    color: '#ffffff',
    editable: false,
    id: `preview-booking-extra-${index}`,
    classNames: ['preview-booking'],
  }));

  const exceedsSchedule = Boolean(
    previewStart &&
      scheduleEnd &&
      (() => {
        // For 24-hour spaces, no closing time restrictions
        if (sched.openAllDay) {
          return false;
        }
        
        const bookingEnd = new Date(previewStart.getTime() + newDuration * 60000);
        
        // For overnight schedules, the scheduleEnd is already calculated for the next day
        // For normal schedules, check if booking extends beyond the end time
        return bookingEnd.getTime() > scheduleEnd.getTime();
      })()
  );

  // Create working hours events to show on the grid
  const workingHoursEvents = useMemo(() => {
    if (!room?.spaceWeeklySchedule) return [];
    
    const events = [];
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    
    // Get the visible week's date range from calendar
    const calendarApi = calendarRef.current?.getApi();
    const visibleStart = calendarApi?.view?.activeStart;
    if (!visibleStart) return [];
    
    const startOfWeek = new Date(visibleStart);
    // Adjust to Monday start
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1);
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(startOfWeek);
      dayDate.setDate(startOfWeek.getDate() + i);
      
      const schedule = room.spaceWeeklySchedule[dayNames[dayDate.getDay()]];
      
      if (schedule && !schedule.dayOff) {
        // Skip past days entirely
        const dayOnly = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
        if (dayOnly < todayStart) {
          continue;
        }

        let startTime: Date;
        let endTime: Date;

        if (schedule.openAllDay) {
          // For 24-hour days, cover the entire day
          startTime = new Date(dayDate);
          startTime.setHours(0, 0, 0, 0);
          endTime = new Date(dayDate);
          endTime.setHours(24, 0, 0, 0);
        } else {
          // For regular days, use the schedule times
          const [startHour, startMinute] = schedule.periodStart.split(':').map(Number);
          const [endHour, endMinute] = schedule.periodEnd.split(':').map(Number);
          
          startTime = new Date(dayDate);
          startTime.setHours(startHour, startMinute, 0, 0);
          
          endTime = new Date(dayDate);
          endTime.setHours(endHour, endMinute, 0, 0);
        }
        
        // If it's today, clip working-hours start to now to avoid green in the past
        if (dayOnly.getTime() === todayStart.getTime() && startTime < now) {
          startTime = new Date(now);
        }

        // If the clipped start is after end, skip
        if (startTime >= endTime) {
          continue;
        }

        // Add working hours indicator only for future portion
        events.push({
          id: `working-hours-${i}`,
          title: '',
          start: startTime,
          end: endTime,
          backgroundColor: 'rgb(94 117 241 / 40%)',
          textColor: '#ffffff',
          classNames: ['working-hours-indicator'],
          display: 'background'
        });
      }
    }
    
    return events;
  }, [room?.spaceWeeklySchedule, visibleRangeVersion]);

  // Create blocked slot events to show unavailable times
  const blockedSlotEvents = useMemo(() => {
    if (!room?.spaceWeeklySchedule) return [];
    
    const events = [];
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    
    // Get the visible week's date range from calendar
    const calendarApi = calendarRef.current?.getApi();
    const visibleStart = calendarApi?.view?.activeStart;
    if (!visibleStart) return [];
    
    const startOfWeek = new Date(visibleStart);
    // Adjust to Monday start
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1);
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const [slotMinH, slotMinM] = slotMin.split(':').map(Number);
    const [slotMaxH, slotMaxM] = slotMax.split(':').map(Number);
    
    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(startOfWeek);
      dayDate.setDate(startOfWeek.getDate() + i);
      
      const schedule = room.spaceWeeklySchedule[dayNames[dayDate.getDay()]];
      
      if (schedule && !schedule.dayOff && !schedule.openAllDay) {
        // Create blocked events for times before opening
        const [startHour, startMinute] = schedule.periodStart.split(':').map(Number);
        const [endHour, endMinute] = schedule.periodEnd.split(':').map(Number);
        const dayOnly = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
        
        // Skip past days entirely (covered by pastBlockedEvents)
        if (dayOnly < todayStart) {
          continue;
        }
        
        // Block times before opening (from visible grid start through opening)
        const openingStart = startHour * 60 + startMinute;
        const gridStart = slotMinH * 60 + (slotMinM || 0);
        if (openingStart > gridStart) {
          const blockedStart = new Date(dayDate);
          blockedStart.setHours(slotMinH, slotMinM || 0, 0, 0);
          const startCandidate =
            dayOnly.getTime() === todayStart.getTime() ? new Date(Math.max(blockedStart.getTime(), now.getTime())) : blockedStart;
          
          const blockedEnd = new Date(dayDate);
          blockedEnd.setHours(startHour, startMinute, 0, 0);
          
          if (startCandidate < blockedEnd) {
            events.push({
              id: `blocked-before-${i}`,
              title: '',
              start: startCandidate,
              end: blockedEnd,
              backgroundColor: 'transparent',
              classNames: ['blocked-slot'],
              display: 'background',
            });
          }
        }
        
        // Block times after closing through the visible grid end
        const closingEnd = endHour * 60 + endMinute;
        const gridEnd = slotMaxH * 60 + (slotMaxM || 0);
        if (closingEnd < gridEnd) {
          const blockedStart = new Date(dayDate);
          blockedStart.setHours(endHour, endMinute, 0, 0);
          
          const blockedEnd = new Date(dayDate);
          blockedEnd.setHours(slotMaxH, slotMaxM || 0, 0, 0);
          
          if (blockedStart < blockedEnd) {
            events.push({
              id: `blocked-after-${i}`,
              title: '',
              start: blockedStart,
              end: blockedEnd,
              backgroundColor: 'transparent',
              classNames: ['blocked-slot'],
              display: 'background',
            });
          }
        }
      }
    }
    
    return events;
  }, [room?.spaceWeeklySchedule, visibleRangeVersion, slotMin, slotMax]);

  // Create blocked overlay for past times (previous days fully, today up to now)
  const pastBlockedEvents = useMemo(() => {
    const events: any[] = [];
    
    // Get the visible week's date range from calendar
    const calendarApi = calendarRef.current?.getApi();
    const visibleStart = calendarApi?.view?.activeStart;
    if (!visibleStart) return [];
    
    const startOfWeek = new Date(visibleStart);
    // Adjust to Monday start
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(startOfWeek);
      dayDate.setDate(startOfWeek.getDate() + i);
      const dayOnly = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());

      if (dayOnly < today) {
        const start = new Date(dayOnly);
        start.setHours(0, 0, 0, 0);
        const end = new Date(dayOnly);
        end.setHours(23, 59, 59, 999);
        events.push({
          id: `blocked-past-day-${i}`,
          title: '',
          start,
          end,
          backgroundColor: 'transparent',
          classNames: ['blocked-slot'],
          display: 'background'
        });
      } else if (dayOnly.getTime() === today.getTime()) {
        const start = new Date(dayOnly);
        start.setHours(0, 0, 0, 0);
        const end = new Date(now);
        if (end > start) {
          events.push({
            id: `blocked-past-today`,
            title: '',
            start,
            end,
            backgroundColor: 'transparent',
            classNames: ['blocked-slot'],
            display: 'background'
          });
        }
      }
    }

    return events;
  }, [visibleRangeVersion]);

  // Create blocked overlay for day-off events
  const dayOffEvents = useMemo(() => {
    const events: any[] = [];
    
    // Get the visible week's date range from calendar
    const calendarApi = calendarRef.current?.getApi();
    const visibleStart = calendarApi?.view?.activeStart;
    if (!visibleStart) return [];
    
    const startOfWeek = new Date(visibleStart);
    // Adjust to Monday start
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1);

    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(startOfWeek);
      dayDate.setDate(startOfWeek.getDate() + i);
      const dayOfWeek = dayDate.getDay();
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
      const scheduleKey = dayNames[dayOfWeek];
      const schedule = room?.spaceWeeklySchedule?.[scheduleKey];

      if (schedule?.dayOff) {
        const start = new Date(dayDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(dayDate);
        end.setHours(23, 59, 59, 999);
        events.push({
          id: `blocked-day-off-${i}`,
          title: '',
          start,
          end,
          backgroundColor: 'transparent',
          classNames: ['blocked-slot'],
          display: 'background'
        });
      }
    }

    return events;
  }, [room?.spaceWeeklySchedule, visibleRangeVersion]);

  const eventsToShow = previewEvt
    ? [
        ...workingHoursEvents,
        ...existingEvents,
        previewEvt,
        ...extraPreviewEvents,
        ...blockedSlotEvents,
        ...pastBlockedEvents,
        ...dayOffEvents,
      ]
    : [
        ...workingHoursEvents,
        ...(existingEvents || []),
        ...extraPreviewEvents,
        ...blockedSlotEvents,
        ...pastBlockedEvents,
        ...dayOffEvents,
      ];
  const hasConflict = Boolean(
    previewStart &&
      previewEvt &&
      existingEvents.some(
        (ev) => ev.startDate < previewEvt.end && ev.endDate > previewStart
      )
  );

  const extraSessionProblem = useMemo<'overlap' | 'unavailable' | null>(() => {
    if (!showRecurringControls || !previewStart || extraStarts.length === 0) return null;
    const durationMinutes = newDuration > 0 ? newDuration : 60;
    const series = [previewStart, ...extraStarts].map((start) => ({ start, durationMinutes }));
    if (bookingSessionsOverlap(series)) return 'overlap';
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const zone = room?.spaceTimeZone?.trim() || Intl.DateTimeFormat().resolvedOptions().timeZone;
    for (const start of extraStarts) {
      const schedule = room?.spaceWeeklySchedule?.[dayNames[start.getDay()]];
      if (schedule?.dayOff) return 'unavailable';
      const end = new Date(start.getTime() + durationMinutes * 60000);
      const booked = calendarEvents.some((ev) => {
        const localStart = utcToZonedTime(parseBookingCalendarInstant(ev.startTime), zone);
        const localEnd = utcToZonedTime(parseBookingCalendarInstant(ev.endTime), zone);
        return localStart < end && localEnd > start;
      });
      if (booked) return 'unavailable';
    }
    return null;
  }, [
    calendarEvents,
    extraStarts,
    newDuration,
    previewStart,
    room?.spaceTimeZone,
    room?.spaceWeeklySchedule,
    showRecurringControls,
  ]);

  const formattedBooking = previewStart && newTime
    ? (() => {
        const locale = t('calendar.locale', 'en');
        const dateFormatter = new Intl.DateTimeFormat(locale, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
        const timeFormatter = new Intl.DateTimeFormat(locale, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
        const bookingEnd = new Date(previewStart.getTime() + newDuration * 60000);
        return (
          dateFormatter.format(previewStart) +
          ' at ' +
          timeFormatter.format(previewStart) +
          ' - ' +
          timeFormatter.format(bookingEnd)
        );
      })()
    : '';

  // Effect to notify parent about validity changes
  useEffect(() => {
    const getSchedule = (date: Date) => {
      const dayOfWeek = date.getDay();
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
      const scheduleKey = dayNames[dayOfWeek];
      return room?.spaceWeeklySchedule?.[scheduleKey];
    };
    const isWithinHours = (() => {
      if (!newTime || !newDate) return false;
      const schedule = getSchedule(newDate);
      if (!schedule || schedule.dayOff) return false;
      if (schedule.openAllDay) return true;
      const selectedTimeInMinutes = newTime.getHours() * 60 + newTime.getMinutes();
      const [startHour, startMinute] = schedule.periodStart.split(':').map(Number);
      const [endHour, endMinute] = schedule.periodEnd.split(':').map(Number);
      const startTimeInMinutes = startHour * 60 + startMinute;
      const endTimeInMinutes = endHour * 60 + endMinute;
      return endTimeInMinutes < startTimeInMinutes
        ? selectedTimeInMinutes >= startTimeInMinutes || selectedTimeInMinutes <= endTimeInMinutes
        : selectedTimeInMinutes >= startTimeInMinutes && selectedTimeInMinutes <= endTimeInMinutes;
    })();
    const startsBefore = (() => {
      if (!newTime || !newDate) return false;
      const schedule = getSchedule(newDate);
      if (!schedule || schedule.dayOff || schedule.openAllDay) return false;
      const selectedTimeInMinutes = newTime.getHours() * 60 + newTime.getMinutes();
      const [startHour, startMinute] = schedule.periodStart.split(':').map(Number);
      const startTimeInMinutes = startHour * 60 + startMinute;
      return selectedTimeInMinutes < startTimeInMinutes;
    })();
    const isValid = Boolean(
      previewStart &&
      !hasConflict &&
      !exceedsSchedule &&
      !sched.dayOff &&
      isWithinHours &&
      !startsBefore &&
      !extraSessionProblem
    );
    onValidityChange(isValid);
  }, [previewStart, hasConflict, exceedsSchedule, onValidityChange, sched.dayOff, newTime, newDate, room?.spaceWeeklySchedule, extraSessionProblem]);

  const handleDateChange = (date: Date | null) => {
    if (!date) {
    setNewDate(date);
      setDatePickerValue(date);
      return;
    }

    const sameCalendarDay =
      datePickerValue != null &&
      date.getFullYear() === datePickerValue.getFullYear() &&
      date.getMonth() === datePickerValue.getMonth() &&
      date.getDate() === datePickerValue.getDate();
    if (sameCalendarDay) {
      return;
    }

    // Check if the selected date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      // If it's a past date, revert to the current valid date
      setDatePickerValue(newDate);
      return;
    }

    // Check if the selected date is a day off
    const dayOfWeek = date.getDay();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const scheduleKey = dayNames[dayOfWeek];
    const schedule = room?.spaceWeeklySchedule?.[scheduleKey];
    
    if (schedule?.dayOff) {
      // If it's a day off, revert the date picker back to the previous valid date
      setDatePickerValue(newDate);
      setShowDayOffWarning(true);
      return;
    }

    // Only update both values if it's not a day off
    setNewDate(date);
    setDatePickerValue(date);
    setShowDayOffWarning(false);
    
    // Check if the current time is within working hours for the new date
    if (newTime && date) {
      const isWithinHours = isWithinWorkingHours(newTime, date);
      const startsBefore = startsBeforeOpening(newTime, date);
      setShowBeforeOpeningWarning(startsBefore);
      setShowWorkingHoursWarning(!isWithinHours && !startsBefore);
    }
    
    if (onBookingConfirmed && date && newTime) {
      const combinedDateTime = new Date(date);
      combinedDateTime.setHours(newTime.getHours(), newTime.getMinutes());
      onBookingConfirmed({
        date,
        time: combinedDateTime,
        duration: newDuration,
        recurringWeeks,
      });
    }
  };

  const handleTimeChange = (time: Date | null) => {
    setNewTime(time);
    
    // Check if the selected time is within working hours
    if (time && newDate) {
      const isWithinHours = isWithinWorkingHours(time, newDate);
      const startsBefore = startsBeforeOpening(time, newDate);
      
      console.log('Time change debug:', {
        selectedTime: time.toTimeString(),
        selectedDate: newDate.toDateString(),
        isWithinHours,
        startsBefore,
        willShowWorkingHoursWarning: !isWithinHours && !startsBefore,
        willShowBeforeOpeningWarning: startsBefore
      });
      
      // Show "before opening" warning if it starts before opening
      // Otherwise show "working hours" warning if it's outside working hours
      setShowBeforeOpeningWarning(startsBefore);
      setShowWorkingHoursWarning(!isWithinHours && !startsBefore);
      
      console.log('Warning states set:', {
        showBeforeOpeningWarning: startsBefore,
        showWorkingHoursWarning: !isWithinHours && !startsBefore
      });
    } else {
      setShowWorkingHoursWarning(false);
      setShowBeforeOpeningWarning(false);
    }
    
    if (onBookingConfirmed && newDate && time) {
      const combinedDateTime = new Date(newDate);
      combinedDateTime.setHours(time.getHours(), time.getMinutes());
      onBookingConfirmed({
        date: newDate,
        time: combinedDateTime,
        duration: newDuration,
        recurringWeeks,
      });
    }
  };

  const handleDurationChange = (duration: number) => {
    setNewDuration(duration);
    if (onBookingConfirmed && newDate && newTime) {
      const combinedDateTime = new Date(newDate);
      combinedDateTime.setHours(newTime.getHours(), newTime.getMinutes());
      onBookingConfirmed({
        date: newDate,
        time: combinedDateTime,
        duration,
        recurringWeeks,
      });
    }
  };

  const handleRecurringChange = (weeks: RecurringWeeks) => {
    if (!previewStart) {
      setExtraStarts([]);
      return;
    }
    const nextExtras = resizeWeeklySessions(
      previewStart,
      extraStarts,
      weeks && weeks > 1 ? weeks : 1,
    );
    setExtraStarts(nextExtras);
    if (onBookingConfirmed && newDate) {
      onBookingConfirmed({
        date: newDate,
        time: previewStart,
        duration: newDuration,
        recurringWeeks: nextExtras.length > 0 ? nextExtras.length + 1 : undefined,
        sessionStarts: [previewStart, ...nextExtras],
      });
    }
  };

  const applySessionStarts = useCallback(
    (next: Date[]) => {
      if (next.length === 0 || !previewStart) return;
      const [first, ...rest] = next;
      setExtraStarts(rest);
      const firstChanged =
        first.getFullYear() !== previewStart.getFullYear() ||
        first.getMonth() !== previewStart.getMonth() ||
        first.getDate() !== previewStart.getDate() ||
        first.getHours() !== previewStart.getHours() ||
        first.getMinutes() !== previewStart.getMinutes();
      if (!firstChanged) return;
      const day = new Date(first.getFullYear(), first.getMonth(), first.getDate());
      setNewDate(day);
      setDatePickerValue(day);
      setNewTime(first);
    },
    [previewStart],
  );

  useEffect(() => {
    if (!onAddSessionControlsChange) return undefined;
    const canAdd =
      Boolean(showRecurringControls && previewStart) &&
      [previewStart, ...extraStarts].length < BOOKING_REPEAT_MAX_SESSIONS;
    if (!canAdd || !previewStart) {
      onAddSessionControlsChange(null);
      return () => onAddSessionControlsChange(null);
    }
    onAddSessionControlsChange({
      canAdd: true,
      addSession: () => {
        applySessionStarts(appendBookingSession([previewStart, ...extraStarts]));
      },
    });
    return () => onAddSessionControlsChange(null);
  }, [
    onAddSessionControlsChange,
    showRecurringControls,
    previewStart,
    extraStarts,
    applySessionStarts,
  ]);

  // Handle date selection from calendar
  const handleDateSelect = (selectInfo: DateSelectArg) => {
    const startDate = selectInfo.start;
    const endDate = selectInfo.end;
    
    // Debug logging to understand what FullCalendar is returning
    console.log('=== DRAG DEBUG ===');
    console.log('FullCalendar selectInfo:', {
      startDate: startDate,
      endDate: endDate,
      startDateISO: startDate.toISOString(),
      endDateISO: endDate.toISOString(),
      startDateLocal: startDate.toLocaleString(),
      endDateLocal: endDate.toLocaleString(),
      spaceTimeZone: room?.spaceTimeZone,
      userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      startDateTimezoneOffset: startDate.getTimezoneOffset(),
      endDateTimezoneOffset: endDate.getTimezoneOffset()
    });
    
    
    // Check if the selected date is a day off
    const selectedDayOfWeek = startDate.getDay();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const selectedScheduleKey = dayNames[selectedDayOfWeek];
    const selectedSchedule = room?.spaceWeeklySchedule?.[selectedScheduleKey];
    
    // Prevent selecting on days off
    if (selectedSchedule?.dayOff) {
      // Clear the selection and return early
      const calendarApi = selectInfo.view.calendar;
      calendarApi.unselect();
      return;
    }
    
    // Calculate duration in minutes (fixed lesson length overrides drag extent)
    let durationMinutes = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60));
    const selectionEnd =
      fixedDurationMinutes != null && fixedDurationMinutes > 0
        ? new Date(startDate.getTime() + fixedDurationMinutes * 60000)
        : endDate;
    if (fixedDurationMinutes != null && fixedDurationMinutes > 0) {
      durationMinutes = fixedDurationMinutes;
    }

    // Since FullCalendar now works in local time, we can use the dates directly
    const localStartDate = startDate;
    
    console.log('Conversion result:', {
      originalStart: startDate.toISOString(),
      convertedStart: localStartDate.toISOString(),
      originalStartLocal: startDate.toLocaleString(),
      convertedStartLocal: localStartDate.toLocaleString(),
      originalHours: startDate.getHours(),
      convertedHours: localStartDate.getHours()
    });
    
    const selectedDate = new Date(localStartDate.getFullYear(), localStartDate.getMonth(), localStartDate.getDate());
    
    // Validate if the selected time slot is actually available
    const isValidSlot = (() => {
      // Check if it's within working hours
      if (!selectedSchedule || selectedSchedule.dayOff) return false;
      
      if (!selectedSchedule.openAllDay) {
        const [startHour, startMinute] = selectedSchedule.periodStart.split(':').map(Number);
        const [endHour, endMinute] = selectedSchedule.periodEnd.split(':').map(Number);
        
        const windowStart = new Date(selectedDate);
        windowStart.setHours(startHour, startMinute, 0, 0);
        
        let windowEnd = new Date(selectedDate);
        windowEnd.setHours(endHour, endMinute, 0, 0);
        
        // Handle overnight schedules
        if (endHour < startHour || (endHour === startHour && endMinute < startMinute)) {
          windowEnd.setDate(windowEnd.getDate() + 1);
        }
        
        // Check if selection is within working window
        if (startDate < windowStart || selectionEnd > windowEnd) {
          return false;
        }
      }
      
      // Check for conflicts with existing bookings
      const conflicts = existingEvents.some(ev => ev.start < selectionEnd && ev.end > startDate);
      if (conflicts) return false;
      
      // Check if it's not in the past (for today)
      const now = new Date();
      if (selectedDate.toDateString() === now.toDateString() && startDate < now) {
        return false;
      }
      
      return true;
    })();
    
    if (!isValidSlot) {
      // Clear the selection and return early - slot is not available
      const calendarApi = selectInfo.view.calendar;
      calendarApi.unselect();
      return;
    }
    
    setNewDate(selectedDate);
    setDatePickerValue(selectedDate); // Also update the date picker value
    setNewTime(localStartDate);
    setNewDuration(durationMinutes);
    setShowDayOffWarning(false); // Clear day-off warning when selecting valid date
    
    // Notify parent component
    if (onBookingConfirmed) {
      onBookingConfirmed({
        date: selectedDate,
        time: localStartDate,
        duration: durationMinutes,
        recurringWeeks,
      });
    }
    
    // Clear the selection
    const calendarApi = selectInfo.view.calendar;
    calendarApi.unselect();
  };

  // Handle date click
  const handleDateClick = (clickInfo: any) => {
    const clickedDate = clickInfo.date;
    const now = new Date();
    
    // Prevent selecting dates in the past
    if (clickedDate < now) {
      return;
    }
    
    // Check if the clicked date is a day off
    const clickedDayOfWeek = clickedDate.getDay();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const clickedScheduleKey = dayNames[clickedDayOfWeek];
    const clickedSchedule = room?.spaceWeeklySchedule?.[clickedScheduleKey];
    
    // Prevent clicking on days off
    if (clickedSchedule?.dayOff) {
      return;
    }
    
    // Since FullCalendar now works in local time, we can use the dates directly
    const clickedDateOnly = new Date(clickedDate.getFullYear(), clickedDate.getMonth(), clickedDate.getDate());
    setNewDate(clickedDateOnly);
    setDatePickerValue(clickedDateOnly); // Also update the date picker value
    setNewTime(clickedDate);
    setShowDayOffWarning(false); // Clear day-off warning when clicking valid date
    
    // Notify parent component
    if (onBookingConfirmed) {
      onBookingConfirmed({
        date: clickedDateOnly,
        time: clickedDate,
        duration: newDuration,
        recurringWeeks,
      });
    }
  };

  // Function to determine if a day is a day off
  const isDayOff = (date: Date): boolean => {
    const dayOfWeek = date.getDay();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const scheduleKey = dayNames[dayOfWeek];
    const schedule = room?.spaceWeeklySchedule?.[scheduleKey];
    return schedule?.dayOff || false;
  };

  // Function to check if selected time is within working hours
  const isWithinWorkingHours = (time: Date, date: Date): boolean => {
    if (!time || !date) return false;
    
    const dayOfWeek = date.getDay();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const scheduleKey = dayNames[dayOfWeek];
    const schedule = room?.spaceWeeklySchedule?.[scheduleKey];
    
    if (!schedule || schedule.dayOff) return false;
    if (schedule.openAllDay) return true;
    
    const selectedHour = time.getHours();
    const selectedMinute = time.getMinutes();
    const selectedTimeInMinutes = selectedHour * 60 + selectedMinute;
    
    const [startHour, startMinute] = schedule.periodStart.split(':').map(Number);
    const [endHour, endMinute] = schedule.periodEnd.split(':').map(Number);
    const startTimeInMinutes = startHour * 60 + startMinute;
    const endTimeInMinutes = endHour * 60 + endMinute;
    
    // Handle overnight schedules (e.g., 10:00 to 03:00)
    if (endTimeInMinutes < startTimeInMinutes) {
      // Overnight schedule - check if time is after start OR before end
      return selectedTimeInMinutes >= startTimeInMinutes || selectedTimeInMinutes <= endTimeInMinutes;
    } else {
      // Regular schedule - check if time is between start and end
      return selectedTimeInMinutes >= startTimeInMinutes && selectedTimeInMinutes <= endTimeInMinutes;
    }
  };

  // Function to check if booking starts before opening hours
  const startsBeforeOpening = (time: Date, date: Date): boolean => {
    if (!time || !date) return false;
    
    const dayOfWeek = date.getDay();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const scheduleKey = dayNames[dayOfWeek];
    const schedule = room?.spaceWeeklySchedule?.[scheduleKey];
    
    console.log('startsBeforeOpening debug:', {
      time: time.toTimeString(),
      date: date.toDateString(),
      dayOfWeek,
      scheduleKey,
      schedule,
      hasSchedule: !!schedule,
      isDayOff: schedule?.dayOff,
      openAllDay: schedule?.openAllDay,
      periodStart: schedule?.periodStart
    });
    
    if (!schedule || schedule.dayOff || schedule.openAllDay) return false;
    
    const selectedHour = time.getHours();
    const selectedMinute = time.getMinutes();
    const selectedTimeInMinutes = selectedHour * 60 + selectedMinute;
    
    const [startHour, startMinute] = schedule.periodStart.split(':').map(Number);
    const startTimeInMinutes = startHour * 60 + startMinute;
    
    const result = selectedTimeInMinutes < startTimeInMinutes;
    
    console.log('startsBeforeOpening calculation:', {
      selectedTimeInMinutes,
      startTimeInMinutes,
      result
    });
    
    return result;
  };

  // Function to add CSS classes to day cells
  const dayCellClassNames = (dateInfo: any) => {
    const classes = [];
    if (isDayOff(dateInfo.date)) {
      classes.push('fc-day-off');
    }
    return classes.join(' ');
  };

  // Handle calendar date range changes
  const handleDatesSet = useCallback((dateInfo: DatesSetArg) => {
    // Track the visible date range and fetch events when it changes
    const newStart = dateInfo.start.getTime();
    const newEnd = dateInfo.end.getTime();
    
    // Only fetch if the range has actually changed
    if (!visibleRangeRef.current || 
        visibleRangeRef.current.start !== newStart || 
        visibleRangeRef.current.end !== newEnd) {
      visibleRangeRef.current = { start: newStart, end: newEnd };
      setVisibleRangeVersion((prev) => prev + 1);
      fetchEventsForRange(dateInfo.start, dateInfo.end);
    }
  }, [fetchEventsForRange]);


  return (
    <Box ref={containerRef} sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Loading state */}
      {!room && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6" color="text.secondary">
            {t('spaceCalendar.loadingRoomData', 'Ładowanie danych pokoju...')}
          </Typography>
        </Box>
      )}

      {/* Date, Time, Duration Fields - Fixed at top */}
      {room && (
        <Box ref={controlsRef} sx={{ 
          mb: 2, 
          pt: 1,
          flexShrink: 0
        }}>
          <LocalizationProvider 
            dateAdapter={AdapterDateFns} 
            adapterLocale={((): Locale => {
              const lang = t('calendar.locale', 'en');
              if (lang === 'pl') return plPL;
              return enGB;
            })()}
          >
            <Box sx={{ 
              display: 'flex', 
              gap: 1.5, 
              alignItems: { xs: 'stretch', md: 'flex-end' },
              flexDirection: { xs: 'column', md: 'row' },
              flexWrap: { xs: 'nowrap', md: 'nowrap' }
            }}>
              {/* Duration / lesson option Section - 50% */}
                  <Box sx={{ 
                order: { xs: 3, md: 0 },
                flex: { xs: '1 1 100%', md: '1 1 50%' },
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'stretch', md: 'center' }
              }}>
                {durationSliderReplacement ? (
                  <Box sx={{ width: { xs: 'calc(100% - 16px)', md: '90%' }, mx: { xs: 1, md: 'auto' }, pt: 1 }}>
                    {durationSliderReplacement}
                  </Box>
                ) : (
                  <>
                <Typography variant="body2" sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  mb: { xs: 0.25, md: 0.5 },
                  lineHeight: 1.2,
                  mx: { xs: 1, md: 0 }
                }}>
                  {t('booking.duration', 'Czas trwania')}: <strong>{formatDuration}</strong>
                </Typography>
                <Slider
                  value={newDuration}
                  onChange={(_, value) => handleDurationChange(Number(value))}
                  min={Math.min(...durationOptions)}
                  max={Math.max(...durationOptions)}
                  step={null}
                  marks={durationOptions.map(mins => ({
                    value: mins,
                    label: durationLabels[mins]
                  }))}
                  valueLabelDisplay="off"
                  sx={{
                    width: { xs: 'calc(100% - 16px)', md: '90%' },
                    mx: { xs: 1, md: 'auto' },
                    mt: { xs: 0.5, md: 0 },
                    mb: 0,
                    '& .MuiSlider-root': {
                      marginTop: { xs: '5px !important', md: '10px !important' },
                      marginBottom: { xs: '5px !important', md: '10px !important' },
                    },
                    '& .MuiSlider-mark': {
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: '#4E67EB',
                    },
                    '& .MuiSlider-markLabel': {
                      fontSize: '0.6rem',
                      fontWeight: 500,
                      color: '#666',
                    },
                    '& .MuiSlider-thumb': {
                      backgroundColor: '#4E67EB',
                      width: 12,
                      height: 12,
                    },
                    '& .MuiSlider-track': {
                      backgroundColor: '#4E67EB',
                      height: 2,
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: '#e0e0e0',
                      height: 2,
                    },
                  }}
                />
                  </>
                )}
              </Box>

              {/* Session count — Add another / stepper cover single vs multi */}
              {showRecurringControls && (
                <Box sx={{
                  order: { xs: 4, md: 0 },
                  flex: { xs: '1 1 100%', md: '0 0 auto' },
                  minWidth: { xs: '100%', md: 'auto' },
                  display: 'flex',
                  alignItems: 'center',
                  pt: { xs: 0, md: 1 },
                }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 999,
                      px: 0.35,
                      py: 0.5,
                      width: 'fit-content',
                      bgcolor: 'background.paper',
                      minHeight: 40,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => {
                        const current = recurringWeeks ?? 1;
                        const next = Math.max(1, current - 1);
                        handleRecurringChange(next <= 1 ? undefined : next);
                      }}
                      disabled={(recurringWeeks ?? 1) <= 1}
                      aria-label={t('booking.decreaseOccurrences', 'Decrease occurrences')}
                      sx={{ p: 0.45 }}
                    >
                      <RemoveIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                    <Typography variant="body2" sx={{ minWidth: 36, textAlign: 'center', fontWeight: 600, fontSize: '0.78rem' }}>
                      {`${recurringWeeks ?? 1}x`}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const current = recurringWeeks ?? 1;
                        const next = Math.min(12, current + 1);
                        handleRecurringChange(next <= 1 ? undefined : next);
                      }}
                      disabled={(recurringWeeks ?? 1) >= 12}
                      aria-label={t('booking.increaseOccurrences', 'Increase occurrences')}
                      sx={{ p: 0.45 }}
                    >
                      <AddIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Box>
                </Box>
              )}

              {/* Date / time stay in the strip for a single session only */}
              {extraStarts.length === 0 ? (
                <>
                  <Box sx={{
                    order: { xs: 1, md: 0 },
                    flex: { xs: '1 1 100%', md: '1 1 23%' },
                    minWidth: 0,
                    pt: 1
                  }}>
                    <DatePicker
                      value={datePickerDayValue}
                      onChange={handleDateChange}
                      format="dd/MM/yyyy"
                      minDate={minSelectableDate}
                      shouldDisableDate={(day) =>
                        scheduleForCalendarDate(room?.spaceWeeklySchedule, day)?.dayOff === true
                      }
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: { width: '100%' },
                          label: t('booking.date', 'Data')
                        },
                        popper: {
                          sx: {
                            '& .MuiPaper-root': {
                              backgroundColor: '#1F2129',
                              color: '#ECECFB',
                            },
                            '& .MuiPickersCalendarHeader-label, & .MuiDayCalendar-weekDayLabel': {
                              color: '#ECECFB',
                            },
                            '& .MuiPickersDay-root': {
                              color: '#ECECFB',
                            },
                            '& .MuiPickersDay-root.Mui-selected': {
                              backgroundColor: 'rgb(78,103,235)',
                              color: '#ffffff',
                            },
                          }
                        },
                        mobilePaper: {
                          sx: {
                            backgroundColor: '#1F2129',
                            color: '#ECECFB',
                          }
                        },
                        desktopPaper: {
                          sx: {
                            backgroundColor: '#1F2129',
                            color: '#ECECFB',
                          }
                        }
                      }}
                    />
                  </Box>

                  <Box sx={{
                    order: { xs: 2, md: 0 },
                    flex: { xs: '1 1 100%', md: '1 1 18%' },
                    minWidth: 0,
                    pt: 1
                  }}>
                    <FormControl size="small" sx={{ width: '100%' }}>
                      <InputLabel>{t('booking.time', 'Godzina')}</InputLabel>
                      <Select
                        value={newTime ? `${newTime.getHours().toString().padStart(2,'0')}:${newTime.getMinutes().toString().padStart(2,'0')}` : ''}
                        label={t('booking.time', 'Godzina')}
                        onChange={(e) => {
                          const [hh, mm] = String(e.target.value).split(':').map(Number);
                          const d = new Date(newDate || new Date());
                          d.setHours(hh, mm, 0, 0);
                          handleTimeChange(d);
                        }}
                        displayEmpty
                      >
                        {availableStartTimes.length === 0 && (
                          <MenuItem value="" disabled>{t('spaceCalendar.noTimes', 'Brak dostępnych godzin')}</MenuItem>
                        )}
                        {availableStartTimes.map((d) => {
                          const hh = d.getHours().toString().padStart(2, '0');
                          const mm = d.getMinutes().toString().padStart(2, '0');
                          const key = `${hh}:${mm}`;
                          return (
                            <MenuItem key={key} value={key}>{key}</MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Box>
                </>
              ) : null}
                </Box>
              </LocalizationProvider>
            </Box>
          )}

          {showRecurringControls && previewStart && extraStarts.length > 0 ? (
            <Box
              sx={{
                width: '100%',
                mt: 1.5,
                flex: '1 1 auto',
                minHeight: 0,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25, fontWeight: 600 }}>
                {t('booking.sessionsCount', '{{count}} sessions', {
                  count: extraStarts.length + 1,
                })}
              </Typography>
              <BookingOccurrenceEditor
                starts={[previewStart, ...extraStarts]}
                durationMinutes={newDuration}
                minutesStep={startTimeStepMinutes}
                layout="pills"
                showAddButton={!onAddSessionControlsChange}
                onChange={applySessionStarts}
                isSessionAvailable={(start) => {
                  if (extraSessionProblem === 'overlap') return false;
                  const durationMinutes = newDuration > 0 ? newDuration : 60;
                  const end = new Date(start.getTime() + durationMinutes * 60_000);
                  const dayNames = [
                    'sunday',
                    'monday',
                    'tuesday',
                    'wednesday',
                    'thursday',
                    'friday',
                    'saturday',
                  ] as const;
                  const schedule = room?.spaceWeeklySchedule?.[dayNames[start.getDay()]];
                  if (schedule?.dayOff) return false;
                  const zone =
                    room?.spaceTimeZone?.trim() || Intl.DateTimeFormat().resolvedOptions().timeZone;
                  return !calendarEvents.some((ev) => {
                    const localStart = utcToZonedTime(parseBookingCalendarInstant(ev.startTime), zone);
                    const localEnd = utcToZonedTime(parseBookingCalendarInstant(ev.endTime), zone);
                    return localStart < end && localEnd > start;
                  });
                }}
              />
              {extraSessionProblem === 'unavailable' ? (
                <Alert severity="error" sx={{ borderRadius: 2, mt: 1 }}>
                  {t(
                    'booking.sessionUnavailable',
                    'One of these sessions is on a closed day or already booked.',
                  )}
                </Alert>
              ) : null}
            </Box>
          ) : null}

      {closedRequestedDateLabel ? (
        <Alert severity="info" sx={{ mb: 1.5, flexShrink: 0, borderRadius: 2 }}>
          {t(
            'spaceCalendar.openedOnClosedDay',
            '{{date}} is a day off for this space. The next open day is selected.',
            { date: closedRequestedDateLabel },
          )}
        </Alert>
      ) : null}

      {/* Calendar Section - Takes remaining space (hidden while editing multiple sessions) */}
      {!(showRecurringControls && extraStarts.length > 0) && (() => {
        const hasValidRoom = room && room.id;
        const hasValidDate = newDate && newDate instanceof Date && !isNaN(newDate.getTime());
        return Boolean(hasValidRoom && hasValidDate);
      })() ? (
        <Box sx={{
          flex: '1 1 auto', // Take all available space
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,            // <-- critical in flex columns
          '.fc': {
            borderRadius: 2,
            overflow: 'hidden',
            fontFamily: 'inherit',
            color: '#1f1f1f !important',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04)',
            background: '#ffffff !important',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            position: 'relative',
          },
            // Calendar toolbar styling
            '& .fc-toolbar': {
              backgroundColor: '#ffffff !important',
              marginBottom: '0.5rem',
              padding: '4px 8px !important',
              position: 'relative',
              zIndex: 2,
              minHeight: '36px !important',
            },
            '& .fc-toolbar-chunk': {
              display: 'flex !important',
              alignItems: 'center !important',
              gap: '12px !important',
            },
            '& .fc-button': {
              backgroundColor: '#f8f9fa !important',
              border: '1px solid #e9ecef !important',
              color: '#495057 !important',
              borderRadius: '6px !important',
              padding: '4px 12px !important',
              fontSize: '0.7rem !important',
              fontWeight: '500 !important',
              textTransform: 'none !important',
              boxShadow: 'none !important',
              transition: 'all 0.2s ease !important',
              minHeight: '28px !important',
              '&:hover': {
                backgroundColor: '#f0f2ff !important',
                borderColor: '#4E67EB !important',
                color: '#4E67EB !important',
              },
              '&:active': {
                backgroundColor: '#e8ebff !important',
                borderColor: '#3d56d9 !important',
                color: '#3d56d9 !important',
              },
              '&.fc-button-active': {
                backgroundColor: '#4E67EB !important',
                borderColor: '#4E67EB !important',
                color: '#ffffff !important',
              },
            },
            '& .fc-toolbar-title': {
              fontSize: '0.65rem !important',
              fontWeight: '600 !important',
              color: '#212529 !important',
              lineHeight: '1.1 !important',
              whiteSpace: 'nowrap !important',
              overflow: 'hidden !important',
              textOverflow: 'ellipsis !important',
              maxWidth: '200px !important',
              '@media (max-width: 768px)': {
                fontSize: '0.8rem !important',
                maxWidth: '180px !important',
                lineHeight: '1.1 !important',
              },
              '@media (max-width: 480px)': {
                fontSize: '0.75rem !important',
                maxWidth: '160px !important',
                lineHeight: '1.0 !important',
              },
            },
            // Force white backgrounds for all calendar elements
            '& .fc-daygrid-day, & .fc-daygrid-day-frame, & .fc-view-harness, & .fc-scrollgrid, & .fc-timegrid, & .fc-daygrid, & .fc-timegrid-body, & .fc-timegrid-slot, & .fc-timegrid-slot-minor, & .fc-timegrid-slot-label, & .fc-daygrid-day-bg, & .fc-daygrid-day-events, & .fc-daygrid-day-top, & .fc-scrollgrid-section, & .fc-scrollgrid-section-header, & .fc-scrollgrid-section-body, & .fc-scrollgrid-section-footer, & .fc-scrollgrid-sync-table td, & .fc-scrollgrid-sync-table th': {
              backgroundColor: '#ffffff !important',
            },
            // Make column borders gray
            '& .fc-scrollgrid-sync-table td, & .fc-scrollgrid-sync-table th, & .fc-timegrid-slot, & .fc-timegrid-slot-minor': {
              borderColor: '#e0e0e0 !important',
            },
            // Ensure day-separating column borders are visible
            '& .fc-scrollgrid-sync-table td': {
              borderRight: '1px solid #e0e0e0 !important',
            },
            '& .fc-scrollgrid-sync-table th': {
              borderRight: '1px solid #e0e0e0 !important',
            },
            // More specific targeting for FullCalendar column borders
            '& .fc-timegrid-col': {
              borderRight: '1px solid #d0d0d0 !important',
            },
            '& .fc-timegrid-slot': {
              borderRight: '1px solid #d0d0d0 !important',
            },
            '& .fc-col-header-cell': {
              borderRight: '1px solid #d0d0d0 !important',
            },
            // Ensure toolbar title is visible in light mode
            '& .fc-toolbar-title, & .fc-toolbar-title *': {
              color: '#212529 !important',
            },
            // Navigation buttons (prev, next, today) - standard
            '& .fc-prev-button, & .fc-next-button, & .fc-today-button': {
              borderRadius: '8px !important',
              padding: '6px 14px !important',
              fontSize: '0.75rem !important',
              fontWeight: '500 !important',
              minWidth: '40px !important',
              height: '36px !important',
              display: 'flex !important',
              alignItems: 'center !important',
              justifyContent: 'center !important',
            },
            // View buttons (week, day) - standard
            '& .fc-timeGridWeek-button, & .fc-timeGridDay-button': {
              borderRadius: '8px !important',
              padding: '6px 16px !important',
              fontSize: '0.75rem !important',
              fontWeight: '500 !important',
              marginLeft: '8px !important',
              minWidth: 'auto !important',
              whiteSpace: 'nowrap !important',
            },
            // Month/Year title button styling
            '& .fc-monthYearTitle-button': {
              backgroundColor: 'transparent !important',
              border: 'none !important',
              color: '#212529 !important',
              fontSize: '1.1rem !important',
              fontWeight: '600 !important',
              padding: '8px 16px !important',
              marginLeft: '8px !important',
              cursor: 'default !important',
              boxShadow: 'none !important',
              transition: 'color 0.2s ease !important',
              '&:hover': {
                backgroundColor: 'transparent !important',
                color: '#495057 !important',
              },
              '&:active': {
                color: '#6c757d !important',
              },
            },
            // Mobile responsive adjustments
            '@media (max-width: 768px)': {
              '& .fc-toolbar': {
                padding: '6px 12px 0px 12px !important',
                marginBottom: '0.5rem !important',
                flexDirection: 'row !important',
                gap: '4px !important',
                flexWrap: 'nowrap !important',
                justifyContent: 'space-between !important',
                minHeight: '32px !important',
              },
              '& .fc-toolbar-chunk': {
                flexWrap: 'nowrap !important',
                gap: '4px !important',
                justifyContent: 'center !important',
                display: 'flex !important',
                alignItems: 'center !important',
                margin: '0 !important',
              },
              '& .fc-toolbar-chunk:first-child': {
                order: 1,
                flex: '0 0 auto !important',
                justifyContent: 'flex-start !important',
                marginLeft: '8px !important',
              },
              '& .fc-toolbar-chunk:nth-child(2)': {
                order: 2,
                flex: '1 1 auto !important',
                textAlign: 'center !important',
                minWidth: '0 !important',
              },
              '& .fc-toolbar-chunk:last-child': {
                order: 3,
                flex: '0 0 auto !important',
                justifyContent: 'flex-end !important',
                marginRight: '8px !important',
              },
              '& .fc-timeGridWeek-button, & .fc-timeGridDay-button': {
                padding: '5px 12px !important',
                fontSize: '0.85rem !important',
                marginLeft: '4px !important',
                borderRadius: '6px !important',
              },
              '& .fc-prev-button, & .fc-next-button, & .fc-today-button': {
                padding: '5px 10px !important',
                fontSize: '0.85rem !important',
                minWidth: '35px !important',
                height: '32px !important',
                borderRadius: '6px !important',
              },
              // Mobile styling for monthYearTitle button
              '& .fc-monthYearTitle-button': {
                fontSize: '0.75rem !important',
                padding: '3px 4px !important',
                marginLeft: '0 !important',
                maxWidth: '100% !important',
                width: 'auto !important',
                overflow: 'hidden !important',
                textOverflow: 'ellipsis !important',
                whiteSpace: 'nowrap !important',
                textAlign: 'center !important',
                lineHeight: '1.0 !important',
              },
              // Mobile event styling
              '& .fc-event': {
                fontSize: '11px !important',
                minHeight: '24px !important',
                padding: '3px 6px !important',
              },
              // Mobile day headers
              '& .fc-col-header-cell-cushion': {
                fontSize: '13px !important',
                padding: '8px 4px !important',
                backgroundColor: '#f0f2ff !important',
                color: '#4E67EB !important',
              },
            },
            // Extra small mobile screens
            '@media (max-width: 480px)': {
              '& .fc-toolbar': {
                padding: '6px 10px 0px 10px !important',
                marginBottom: '0.5rem !important',
                flexDirection: 'row !important',
                gap: '2px !important',
                flexWrap: 'nowrap !important',
                justifyContent: 'space-between !important',
                minHeight: '30px !important',
              },
              '& .fc-toolbar-chunk': {
                justifyContent: 'center !important',
                gap: '2px !important',
                display: 'flex !important',
                alignItems: 'center !important',
                flexWrap: 'nowrap !important',
                margin: '0 !important',
              },
              '& .fc-toolbar-chunk:first-child': {
                justifyContent: 'flex-start !important',
                marginLeft: '6px !important',
              },
              '& .fc-toolbar-chunk:nth-child(2)': {
                flex: '1 1 auto !important',
                textAlign: 'center !important',
                minWidth: '0 !important',
              },
              '& .fc-toolbar-chunk:last-child': {
                justifyContent: 'flex-end !important',
                marginRight: '6px !important',
              },
              '& .fc-monthYearTitle-button': {
                fontSize: '0.7rem !important',
                padding: '2px 3px !important',
                marginLeft: '0 !important',
                maxWidth: '100% !important',
                width: 'auto !important',
                textAlign: 'center !important',
                lineHeight: '1.0 !important',
                whiteSpace: 'nowrap !important',
                overflow: 'hidden !important',
                textOverflow: 'ellipsis !important',
              },
              '& .fc-prev-button, & .fc-next-button, & .fc-today-button': {
                padding: '5px 10px !important',
                fontSize: '0.8rem !important',
                minWidth: '32px !important',
                height: '30px !important',
                borderRadius: '6px !important',
              },
              '& .fc-timeGridWeek-button, & .fc-timeGridDay-button': {
                padding: '4px 10px !important',
                fontSize: '0.8rem !important',
                marginLeft: '3px !important',
                borderRadius: '6px !important',
              },
              // Extra small mobile event styling
              '& .fc-event': {
                fontSize: '10px !important',
                minHeight: '22px !important',
                padding: '2px 4px !important',
              },
              // Extra small mobile day headers
              '& .fc-col-header-cell-cushion': {
                fontSize: '12px !important',
                padding: '6px 2px !important',
                backgroundColor: '#f0f2ff !important',
                color: '#4E67EB !important',
              },
            },
            // Event styling
            '& .fc-event': {
              fontSize: '12px',
              whiteSpace: 'nowrap !important',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: '1.4',
              minHeight: '24px',
              padding: '3px 6px',
              cursor: 'pointer',
              borderRadius: '4px !important',
              border: '1px solid rgba(0,0,0,0.1) !important',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1) !important',
              transition: 'all 0.2s ease !important',
              fontWeight: '500 !important',
              '&:hover': {
                boxShadow: '0 2px 6px rgba(0,0,0,0.15) !important',
              },
            },
            // Time grid styling
            '& .fc-timegrid-axis': {
              width: '50px !important',
              minWidth: '50px !important',
              maxWidth: '50px !important',
              backgroundColor: '#f0f2ff !important',
              borderRight: '2px solid #4E67EB !important',
            },
            '& .fc-timegrid-slot-label-cushion': {
              fontSize: '0.8rem',
              padding: '0 4px !important',
              fontWeight: '500 !important',
              color: '#4E67EB !important',
            },
            // Day headers
            '& .fc-col-header-cell-cushion': {
              fontSize: '14px !important',
              fontWeight: '600 !important',
              padding: '10px 8px !important',
              textAlign: 'center !important',
              textTransform: 'none',
              whiteSpace: 'pre-line !important',
              lineHeight: '1.3 !important',
              backgroundColor: '#f0f2ff !important',
              color: '#4E67EB !important',
              borderBottom: '2px solid #4E67EB !important',
            },
            // Sunday styling - green color for weekend
            '& .fc-col-header-cell:nth-child(1) .fc-col-header-cell-cushion': {
              backgroundColor: '#d1fae5 !important',
              color: '#166534 !important',
              borderBottom: '2px solid #166534 !important',
            },
            // Disable interaction for days off
            '& .fc-daygrid-day.fc-day-off': {
              backgroundColor: 'rgba(0, 0, 0, 0.05) !important',
              cursor: 'not-allowed !important',
              opacity: '0.6 !important',
              position: 'relative !important',
            },
            '& .fc-timegrid-day.fc-day-off': {
              backgroundColor: 'rgba(0, 0, 0, 0.05) !important',
              cursor: 'not-allowed !important',
              opacity: '0.6 !important',
              position: 'relative !important',
            },
            '& .fc-daygrid-day.fc-day-off .fc-daygrid-day-number': {
              color: 'rgba(0, 0, 0, 0.4) !important',
            },
            '& .fc-timegrid-day.fc-day-off .fc-timegrid-slot': {
              backgroundColor: 'rgba(0, 0, 0, 0.05) !important',
              cursor: 'not-allowed !important',
            },
            // Style for existing bookings
            '& .fc-event.existing-booking': {
              backgroundColor: '#dc3545 !important',
              border: '1px solid #dc3545 !important',
              color: '#ffffff !important',
              opacity: '0.9 !important',
              cursor: 'not-allowed !important',
              '&:hover': {
                opacity: '1 !important',
                backgroundColor: '#c82333 !important',
                borderColor: '#c82333 !important',
              },
            },
            '&': {
            position: 'relative'
            },
            // Custom scrollbar styling for the calendar
            '& .fc-scroller': {
              // Webkit browsers (Chrome, Safari, Edge)
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(0, 0, 0, 0.1)',
                borderRadius: '4px',
                '&:hover': {
                  background: 'rgba(0, 0, 0, 0.2)',
                },
              },
              '&::-webkit-scrollbar-corner': {
                background: 'transparent',
              },
              // Firefox
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(0, 0, 0, 0.1) transparent',
            },
          }}>
            {/* Custom CSS for FullCalendar styling */}
            <style>
              {`
                /* Make toolbar more compact */
                .fc-toolbar {
                  padding: 2px 4px !important;
                  margin-bottom: 4px !important;
                  min-height: 24px !important;
                }
                
                .fc-toolbar-chunk {
                  gap: 8px !important;
                }
                
                /* Add more space around prev/next buttons */
                .fc-toolbar-chunk:first-child,
                .fc-toolbar-chunk:last-child {
                  margin: 0 8px !important;
                }
                
                /* Mobile: reduce toolbar spacing */
                @media (max-width: 768px) {
                  .fc-toolbar {
                    padding: 6px 12px 0px 12px !important;
                    margin-bottom: 4px !important;
                    min-height: 32px !important;
                  }
                  
                  .fc-toolbar-chunk {
                    gap: 4px !important;
                    margin: 0 !important;
                  }
                  
                  .fc-toolbar-chunk:first-child {
                    margin-left: 8px !important;
                  }
                  
                  .fc-toolbar-chunk:last-child {
                    margin-right: 8px !important;
                  }
                }
                
                /* Extra small mobile: even more compact */
                @media (max-width: 480px) {
                  .fc-toolbar {
                    padding: 6px 10px 0px 10px !important;
                    margin-bottom: 2px !important;
                    min-height: 28px !important;
                  }
                  
                  .fc-toolbar-chunk {
                    gap: 2px !important;
                  }
                  
                  .fc-toolbar-chunk:first-child {
                    margin-left: 6px !important;
                  }
                  
                  .fc-toolbar-chunk:last-child {
                    margin-right: 6px !important;
                  }
                }
                
                .fc-button {
                  padding: 2px 8px !important;
                  font-size: 0.65rem !important;
                  min-height: 20px !important;
                  line-height: 1 !important;
                }
                
                /* Make prev/next/today buttons much smaller */
                .fc-prev-button,
                .fc-next-button,
                .fc-today-button {
                  padding: 1px 4px !important;
                  font-size: 0.5rem !important;
                  min-height: 12px !important;
                  max-height: 12px !important;
                  line-height: 1 !important;
                  border-radius: 3px !important;
                }
                
                /* Add space between prev and next buttons */
                .fc-prev-button {
                  margin-right: 4px !important;
                }
                
                .fc-next-button {
                  margin-left: 4px !important;
                }
                
                .fc-prev-button .fc-icon,
                .fc-next-button .fc-icon {
                  font-size: 0.4rem !important;
                  line-height: 1 !important;
                }
                
                /* Make day headers more compact - balanced */
                .fc .fc-col-header-cell {
                  padding: 6px 4px !important;
                  height: 28px !important;
                  min-height: 28px !important;
                  max-height: 28px !important;
                }
                
                .fc .fc-col-header-cell-cushion {
                  padding: 2px 6px !important;
                  font-size: 0.75rem !important;
                  line-height: 1.2 !important;
                  border-bottom: none !important;
                  text-decoration: none !important;
                  background-color: transparent !important;
                  background: none !important;
                }
                
                /* Extra specific targeting for header text underlines */
                .fc .fc-scrollgrid .fc-col-header-cell-cushion {
                  border-bottom: none !important;
                  text-decoration: none !important;
                  background-color: transparent !important;
                  background: none !important;
                }
                
                /* Make time slots more compact with 30-min granularity */
                .fc-timegrid-slot {
                  height: 25px !important;
                  min-height: 25px !important;
                  max-height: 25px !important;
                  border-top: 1px solid #e0e0e0 !important;
                }
                
                .fc-timegrid-slot-minor {
                  height: 12px !important;
                  min-height: 12px !important;
                  max-height: 12px !important;
                  border-top: 1px solid #f0f0f0 !important;
                }
                
                .fc-timegrid-slot-label {
                  font-size: 0.65rem !important;
                  padding: 2px 4px !important;
                  line-height: 1 !important;
                }
                
                /* Make events more compact */
                .fc-event {
                  font-size: 0.65rem !important;
                  padding: 2px 4px !important;
                  margin: 0 !important;
                  line-height: 1 !important;
                  min-height: 18px !important;
                }
                
                /* Compact time axis */
                .fc-timegrid-axis {
                  width: 45px !important;
                  font-size: 0.65rem !important;
                  border-right: none !important;
                }
                
                /* More specific targeting for time axis border */
                .fc .fc-timegrid-axis,
                .fc .fc-timegrid-slot-label {
                  border-right: none !important;
                  border-left: none !important;
                }
                
                /* Keep borders for 30-min granularity */
                .fc-timegrid-body {
                  border-spacing: 0 !important;
                }
                
                /* Hide toolbar title (date range between buttons) */
                .fc-toolbar-title {
                  display: none !important;
                }
                
                /* Working hours indicator styling - vibrant green */
                .fc-event.working-hours-indicator,
                .fc-bg-event.working-hours-indicator {
                  background-color: rgb(94 117 241 / 40%) !important;
                  border: none !important; /* avoid double grid borders at intersections */
                  box-shadow: none !important; /* avoid extra edge lines */
                  /* leave a thin transparent gutter so the grid borders show through */
                  padding: 1px !important;
                  background-clip: content-box !important;
                  color: #ffffff !important;
                  font-size: 0.8rem !important;
                  font-weight: 600 !important;
                  pointer-events: auto !important;
                  z-index: 1 !important;
                  border-radius: 0 !important; /* align with grid cells */
                  mix-blend-mode: normal !important;
                }
                
                /* Blocked slots outside working hours - diagonal overlay */
                .fc-event.blocked-slot,
                .fc-bg-event.blocked-slot {
                  background:
                    repeating-linear-gradient(
                      135deg,
                      rgba(108, 117, 125, 0.08),
                      rgba(108, 117, 125, 0.08) 10px,
                      rgba(108, 117, 125, 0.16) 10px,
                      rgba(108, 117, 125, 0.16) 20px
                    ) !important;
                  border: none !important; /* avoid double grid borders */
                  box-shadow: none !important;
                  cursor: not-allowed !important;
                  pointer-events: auto !important;
                  z-index: 2 !important; /* higher than working-hours to override green */
                  border-radius: 0 !important;
                  padding: 1px !important;              /* keep grid borders visible */
                  background-clip: content-box !important;
                }
                
                .fc-timegrid-slot.fc-blocked-slot {
                  background-color: rgba(108, 117, 125, 0.03) !important;
                  cursor: not-allowed !important;
                  position: relative !important;
                }
                
                .fc-timegrid-slot.fc-blocked-slot::after {
                  content: "🚫" !important;
                  position: absolute !important;
                  top: 50% !important;
                  left: 50% !important;
                  transform: translate(-50%, -50%) !important;
                  font-size: 1rem !important;
                  opacity: 0.2 !important;
                  pointer-events: none !important;
                  filter: grayscale(100%) !important;
                }
                
                /* Make time grid expand to fill available space */
                .fc-timegrid-body {
                  height: auto !important;
                }
                
                .fc-timegrid-slots {
                  height: auto !important;
                }
                
                .fc-timegrid-slot {
                  height: auto !important;
                  min-height: 2.5em !important;
                }
                
                /* Resize handle styling - invisible areas */
                .fc-event-resizer {
                  background: transparent !important;
                  border: none !important;
                  width: 100% !important;
                  height: 8px !important;
                  opacity: 1 !important;
                  position: absolute !important;
                  left: 0 !important;
                  right: 0 !important;
                }
                
                /* Top resize handle - full width at top */
                .fc-event-resizer-start {
                  top: 0 !important;
                  height: 8px !important;
                }
                
                /* Bottom resize handle - full width at bottom */
                .fc-event-resizer-end {
                  bottom: 0 !important;
                  height: 8px !important;
                }
                
                .fc-event-resizer:hover {
                  background: transparent !important;
                }
                
                /* Make preview booking event resizable */
                .fc-event[data-event-id="preview-booking"],
                .fc-event.preview-booking {
                  cursor: move !important;
                  background-color: #4E67EB !important;
                  border: 1px solid #4E67EB !important;
                  color: #ffffff !important;
                  font-weight: 500 !important;
                }
                
                .fc-event[data-event-id="preview-booking"] .fc-event-resizer,
                .fc-event.preview-booking .fc-event-resizer {
                  display: block !important;
                }
                
                /* Event body should have move cursor */
                .fc-event {
                  cursor: move !important;
                }
                
                /* More specific selectors for event body */
                .fc-event * {
                  cursor: move !important;
                }
                
                .fc-event:hover {
                  cursor: move !important;
                }
                
                .fc-event:hover * {
                  cursor: move !important;
                }
                
                /* Override FullCalendar's default drag styling */
                .fc-event.fc-event-dragging,
                .fc-event.fc-dragging,
                .fc-event.fc-event-dragging *,
                .fc-event.fc-dragging * {
                  background-color: #4E67EB !important;
                  border-color: #4E67EB !important;
                  opacity: 1 !important;
                }
                
                /* Also target any dragging state classes */
                .fc-event[class*="dragging"],
                .fc-event[class*="dragging"] * {
                  background-color: #4E67EB !important;
                  border-color: #4E67EB !important;
                  opacity: 1 !important;
                }
                
                /* Target the specific preview booking event when dragging */
                .fc-event.preview-booking.fc-event-dragging,
                .fc-event.preview-booking.fc-dragging {
                  background-color: #4E67EB !important;
                  border-color: #4E67EB !important;
                  opacity: 1 !important;
                }
                
                /* Top border should have row-resize cursor */
                .fc-event .fc-event-resizer-start,
                .fc-event .fc-event-resizer-start:hover {
                  cursor: row-resize !important;
                }
                
                /* Bottom border should have row-resize cursor */
                .fc-event .fc-event-resizer-end,
                .fc-event .fc-event-resizer-end:hover {
                  cursor: row-resize !important;
                }
                
                /* Calendar grid hover cursors - use maximum specificity */
                .fc .fc-timegrid-slot {
                  cursor: pointer !important;
                  transition: background-color 0.2s ease !important;
                }
                
                .fc .fc-timegrid-slot:hover {
                  background-color: rgba(78, 103, 235, 0.08) !important;
                  cursor: pointer !important;
                }
                
                /* Override FullCalendar's default cursor behavior with maximum specificity */
                .fc .fc-timegrid-slot-cushion {
                  cursor: pointer !important;
                }
                
                .fc .fc-timegrid-slot-cushion:hover {
                  cursor: pointer !important;
                }
                
                /* Target all child elements with maximum specificity */
                .fc .fc-timegrid-slot * {
                  cursor: pointer !important;
                }
                
                .fc .fc-timegrid-slot:hover * {
                  cursor: pointer !important;
                }
                
                /* Additional FullCalendar overrides */
                .fc-timegrid-body .fc-timegrid-slot {
                  cursor: pointer !important;
                }
                
                .fc-timegrid-body .fc-timegrid-slot:hover {
                  cursor: pointer !important;
                }
                
                .fc-timegrid-body .fc-timegrid-slot * {
                  cursor: pointer !important;
                }
                
                /* Nuclear option - target the entire calendar */
                .fc-timegrid {
                  cursor: pointer !important;
                }
                
                .fc-timegrid * {
                  cursor: pointer !important;
                }
                
                /* Override any FullCalendar default cursor styles */
                .fc-timegrid-slot[style*="cursor"] {
                  cursor: pointer !important;
                }
                
                /* Target the calendar container itself */
                .fc {
                  cursor: pointer !important;
                }
                
                .fc * {
                  cursor: pointer !important;
                }
                
                /* Force cursor on all interactive elements */
                .fc-timegrid-slot,
                .fc-timegrid-slot-cushion,
                .fc-timegrid-slot-lane {
                  cursor: pointer !important;
                }
                
                
                /* Blocked time slots - indicate they cannot be selected */
                .fc-timegrid-slot.fc-blocked-slot,
                .fc-timegrid-slot.fc-blocked-slot *,
                .fc-timegrid-slot.fc-blocked-slot:hover,
                .fc-timegrid-slot.fc-blocked-slot:hover * {
                  cursor: not-allowed !important;
                }
                
                /* Cells with blocked events */
                .fc-timegrid-slot:has(.fc-event.blocked-slot),
                .fc-timegrid-slot:has(.fc-bg-event.blocked-slot),
                .fc-timegrid-slot:has(.fc-event.blocked-slot) *,
                .fc-timegrid-slot:has(.fc-bg-event.blocked-slot) *,
                .fc-timegrid-slot:has(.fc-event.blocked-slot):hover,
                .fc-timegrid-slot:has(.fc-bg-event.blocked-slot):hover,
                .fc-timegrid-slot:has(.fc-event.blocked-slot):hover *,
                .fc-timegrid-slot:has(.fc-bg-event.blocked-slot):hover * {
                  cursor: not-allowed !important;
                }
                
                /* Fallback for browsers without :has() support */
                .fc-timegrid-slot .fc-event.blocked-slot,
                .fc-timegrid-slot .fc-bg-event.blocked-slot,
                .fc-timegrid-slot .fc-event.blocked-slot *,
                .fc-timegrid-slot .fc-bg-event.blocked-slot * {
                  cursor: not-allowed !important;
                }
                
                /* Day off slots */
                .fc-timegrid-slot.fc-day-off,
                .fc-timegrid-slot.fc-day-off *,
                .fc-timegrid-slot.fc-day-off:hover,
                .fc-timegrid-slot.fc-day-off:hover * {
                  cursor: not-allowed !important;
                }
                
                /* Cells with day-off events */
                .fc-timegrid-slot:has(.fc-event.day-off),
                .fc-timegrid-slot:has(.fc-event.day-off) *,
                .fc-timegrid-slot:has(.fc-event.day-off):hover,
                .fc-timegrid-slot:has(.fc-event.day-off):hover * {
                  cursor: not-allowed !important;
                }
                
                /* Fallback for browsers without :has() support */
                .fc-timegrid-slot .fc-event.day-off,
                .fc-timegrid-slot .fc-event.day-off * {
                  cursor: not-allowed !important;
                }
                
                /* Existing bookings - indicate they cannot be selected */
                .fc-event.existing-booking {
                  cursor: not-allowed !important;
                }
                
                /* Cells with existing bookings */
                .fc-timegrid-slot:has(.fc-event.existing-booking) {
                  cursor: not-allowed !important;
                }
                
                /* Fallback for browsers without :has() support */
                .fc-timegrid-slot .fc-event.existing-booking {
                  cursor: not-allowed !important;
                }
                
                /* Working hours indicator - interactive */
                .fc-event.working-hours-indicator,
                .fc-bg-event.working-hours-indicator {
                  cursor: pointer !important;
                }
                
                /* Blocked slot events */
                .fc-event.blocked-slot,
                .fc-bg-event.blocked-slot {
                  cursor: not-allowed !important;
                }
                
                /* Day off events */
                .fc-event.day-off {
                  cursor: not-allowed !important;
                }
                
                /* Calendar day headers - non-interactive */
                .fc-col-header-cell {
                  cursor: default !important;
                }
                
                /* Time axis - non-interactive */
                .fc-timegrid-axis {
                  cursor: default !important;
                }
                
                /* Navigation buttons */
                .fc-button {
                  cursor: pointer !important;
                }
                
                /* Month/Year title - non-interactive */
                .fc-monthYearTitle-button {
                  cursor: default !important;
                }
              `}
            </style>
             <Box sx={{ position: 'relative', height: '100%' }}>
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView={view}
                initialDate={newDate || new Date()}
                firstDay={1}
                headerToolbar={isMobile ? {
                  left: 'prev',
                  center: 'monthYearTitle',
                  right: 'next'
                } : {
                  left: 'timeGridWeek,timeGridDay',
                  center: 'monthYearTitle',
                  right: 'prev,next today'
                }}
                dayHeaderFormat={{
                  weekday: 'short',
                  day: '2-digit'
                }}
                customButtons={{
                  monthYearTitle: {
                    text: (room && formattedBooking && previewStart && !hasConflict && !exceedsSchedule) 
                      ? formattedBooking 
                      : newDate?.toLocaleDateString(t('calendar.locale', 'en'), { month: 'long', year: 'numeric' })
                        .replace(/^\w/, c => c.toUpperCase()) || '',
                    click: function() {
                      // No action needed, just display
                    }
                  }
                }}
              buttonText={{
                today: t('calendar.today', 'Dzisiaj'),
                week: t('calendar.week', 'Tydzień'),
                day: t('calendar.day', 'Dzień')
              }}
              events={eventsToShow || []}
              slotMinTime={slotMin || '06:00:00'}
              slotMaxTime={slotMax || '22:00:00'}
              // timeZone={room.spaceTimeZone} // Removed - handle timezone conversion manually
              nowIndicator
              height={calculatedHeight}
              scrollTime={scrollTime || '06:00:00'}
              scrollTimeReset={false}
              allDaySlot={false}
              handleWindowResize={true}
              selectable={true}
              editable={true}
              eventResizableFromStart={true}
              selectAllow={(selectInfo) => {
                // Prevent multi-day selection - only allow same day selections
                const startDate = new Date(selectInfo.start);
                const endDate = new Date(selectInfo.end);
                const effectiveEnd =
                  fixedDurationMinutes != null && fixedDurationMinutes > 0
                    ? new Date(startDate.getTime() + fixedDurationMinutes * 60000)
                    : endDate;
                const now = new Date();
                
                // Check if selection spans multiple days
                const startDay = startDate.toDateString();
                const endDay = effectiveEnd.toDateString();
                
                if (startDay !== endDay) {
                  return false; // Reject multi-day selections
                }
                // Block any selection that starts in the past
                if (startDate < now) {
                  return false;
                }
                
                // Check if the selection start time is within working hours for that day
                const startTime = startDate.toTimeString().substring(0, 5); // HH:MM format
                return isSlotAvailable(startDate, startTime);
              }}
              select={handleDateSelect}
              dateClick={(clickInfo) => {
                // Check if the clicked time is within working hours for that day
                const clickDate = new Date(clickInfo.date);
                const clickTime = clickDate.toTimeString().substring(0, 5); // HH:MM format
                if (isSlotAvailable(clickDate, clickTime)) {
                  handleDateClick(clickInfo);
                }
              }}
              eventDrop={(dropInfo) => {
                // Handle event dragging to new position
                const startDate = new Date(dropInfo.event.start!);
                const endDate = new Date(dropInfo.event.end!);
                
                // Validate the new position
                const isValidDrop = (() => {
                  // Check if the selected date is a day off
                  const selectedDayOfWeek = startDate.getDay();
                  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
                  const selectedScheduleKey = dayNames[selectedDayOfWeek];
                  const selectedSchedule = room?.spaceWeeklySchedule?.[selectedScheduleKey];
                  
                  if (selectedSchedule?.dayOff) return false;
                  
                  // Check if it's within working hours
                  if (selectedSchedule && !selectedSchedule.openAllDay) {
                    const [startHour, startMinute] = selectedSchedule.periodStart.split(':').map(Number);
                    const [endHour, endMinute] = selectedSchedule.periodEnd.split(':').map(Number);
                    
                    const selectedDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                    const windowStart = new Date(selectedDate);
                    windowStart.setHours(startHour, startMinute, 0, 0);
                    
                    let windowEnd = new Date(selectedDate);
                    windowEnd.setHours(endHour, endMinute, 0, 0);
                    
                    // Handle overnight schedules
                    if (endHour < startHour || (endHour === startHour && endMinute < startMinute)) {
                      windowEnd.setDate(windowEnd.getDate() + 1);
                    }
                    
                    // Check if selection is within working window
                    if (startDate < windowStart || endDate > windowEnd) {
                      return false;
                    }
                  }
                  
                  // Check for conflicts with existing bookings (excluding the preview event)
                  const conflicts = existingEvents.some(ev => 
                    ev.start < endDate && 
                    ev.end > startDate
                  );
                  if (conflicts) return false;
                  
                  // Check if it's not in the past
                  const now = new Date();
                  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                  const selectedDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                  
                  // Reject if the selected date is before today
                  if (selectedDate < today) {
                    return false;
                  }
                  
                  // For today, also check if the time is in the past
                  if (selectedDate.toDateString() === today.toDateString() && startDate < now) {
                    return false;
                  }
                  
                  return true;
                })();
                
                if (!isValidDrop) {
                  dropInfo.revert();
                  return;
                }
                
                // Update form controls with new position
                const selectedDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                const durationMs = endDate.getTime() - startDate.getTime();
                let durationMinutes = Math.round(durationMs / (1000 * 60));
                if (fixedDurationMinutes != null && fixedDurationMinutes > 0) {
                  durationMinutes = fixedDurationMinutes;
                }
                
                setNewDate(selectedDate);
                setDatePickerValue(selectedDate);
                setNewTime(startDate);
                setNewDuration(durationMinutes);
                
                // Notify parent component
                if (onBookingConfirmed) {
                  onBookingConfirmed({
                    date: selectedDate,
                    time: startDate,
                    duration: durationMinutes,
                    recurringWeeks,
                  });
                }
              }}
              eventResize={(resizeInfo) => {
                if (typeof fixedDurationMinutes === 'number' && fixedDurationMinutes > 0) {
                  resizeInfo.revert();
                  return;
                }
                // Handle event resizing to update duration
                const startDate = new Date(resizeInfo.event.start!);
                const endDate = new Date(resizeInfo.event.end!);
                
                // Calculate new duration in minutes
                const durationMs = endDate.getTime() - startDate.getTime();
                const durationMinutes = Math.round(durationMs / (1000 * 60));
                
                // Validate the new duration
                if (durationMinutes < 30) {
                  // Minimum 30 minutes
                  resizeInfo.revert();
                  return;
                }
                
                if (durationMinutes > 1440) {
                  // Maximum 24 hours
                  resizeInfo.revert();
                  return;
                }
                
                // Validate the resized position
                const isValidResize = (() => {
                  // Check if the selected date is a day off
                  const selectedDayOfWeek = startDate.getDay();
                  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
                  const selectedScheduleKey = dayNames[selectedDayOfWeek];
                  const selectedSchedule = room?.spaceWeeklySchedule?.[selectedScheduleKey];
                  
                  if (selectedSchedule?.dayOff) return false;
                  
                  // Check if it's within working hours
                  if (selectedSchedule && !selectedSchedule.openAllDay) {
                    const [startHour, startMinute] = selectedSchedule.periodStart.split(':').map(Number);
                    const [endHour, endMinute] = selectedSchedule.periodEnd.split(':').map(Number);
                    
                    const selectedDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                    const windowStart = new Date(selectedDate);
                    windowStart.setHours(startHour, startMinute, 0, 0);
                    
                    let windowEnd = new Date(selectedDate);
                    windowEnd.setHours(endHour, endMinute, 0, 0);
                    
                    // Handle overnight schedules
                    if (endHour < startHour || (endHour === startHour && endMinute < startMinute)) {
                      windowEnd.setDate(windowEnd.getDate() + 1);
                    }
                    
                    // Check if selection is within working window
                    if (startDate < windowStart || endDate > windowEnd) {
                      return false;
                    }
                  }
                  
                  // Check for conflicts with existing bookings (excluding the preview event)
                  const conflicts = existingEvents.some(ev => 
                    ev.start < endDate && 
                    ev.end > startDate
                  );
                  if (conflicts) return false;
                  
                  return true;
                })();
                
                if (!isValidResize) {
                  resizeInfo.revert();
                  return;
                }
                
                // Check if the resized event conflicts with existing bookings
                const resizedEvent = {
                  start: startDate,
                  end: endDate
                };
                
                const hasConflict = existingEvents.some((ev) => 
                  ev.startDate < resizedEvent.end && ev.endDate > resizedEvent.start
                );
                
                if (hasConflict) {
                  resizeInfo.revert();
                  return;
                }
                
                // Check if the resized event exceeds working hours
                const dayOfWeek = startDate.getDay();
                const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
                const scheduleKey = dayNames[dayOfWeek];
                const schedule = room?.spaceWeeklySchedule?.[scheduleKey];
                
                if (schedule && !schedule.dayOff && !schedule.openAllDay) {
                  const [endHour, endMinute] = schedule.periodEnd.split(':').map(Number);
                  const scheduleEndTime = new Date(startDate);
                  scheduleEndTime.setHours(endHour, endMinute, 0, 0);
                  
                  if (endDate > scheduleEndTime) {
                    resizeInfo.revert();
                    return;
                  }
                }
                
                // Update the duration
                setNewDuration(durationMinutes);
                
                // Notify parent component
                if (onBookingConfirmed && newDate && newTime) {
                  const localDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                  const localTime = new Date(startDate);
                  
                  onBookingConfirmed({
                    date: localDate,
                    time: localTime,
                    duration: durationMinutes,
                    recurringWeeks,
                  });
                }
              }}
              selectMirror={true}
              unselectAuto={false}
              selectMinDistance={1}
              locales={allLocales}
              locale={getFullCalendarLocale(t('calendar.locale', 'en'))}
              expandRows={true}
              views={{
                timeGridDay: {
                  displayEventTime: true,
                  slotDuration: calendarSlotDuration,
                  slotMinTime: slotMin,
                  slotMaxTime: slotMax,
                  dayHeaderFormat: { 
                    day: 'numeric',
                    weekday: 'short'
                  },
                  allDaySlot: false
                },
                timeGridWeek: {
                  displayEventTime: true,
                  slotDuration: calendarSlotDuration,
                  slotMinTime: slotMin,
                  slotMaxTime: slotMax,
                  dayHeaderFormat: { 
                    day: 'numeric',
                    weekday: 'short'
                  },
                  allDaySlot: false
                }
              }}
              slotLabelFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              }}
              eventTimeFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              }}
              dayCellClassNames={dayCellClassNames}
              datesSet={handleDatesSet}
            />
            
            </Box>
          </Box>
      ) : !(showRecurringControls && extraStarts.length > 0) && room ? (
        <Box sx={{ 
          flex: 1,
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '400px',
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          backgroundColor: '#f5f5f5'
        }}>
          <Typography variant="h6" color="text.secondary">
            {t('spaceCalendar.selectDateFirst', 'Wybierz datę, aby zobaczyć kalendarz')}
          </Typography>
        </Box>
      ) : null}

    </Box>
  );
} 
